import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { useNavigate, useParams } from 'react-router-dom';
import { Eye, Trash } from 'react-bootstrap-icons';
import {
    FiBookOpen,
    FiBriefcase,
    FiCheckSquare,
    FiClock,
    FiCreditCard,
    FiDownload,
    FiFileText,
    FiLayers,
    FiMessageSquare,
    FiShield,
    FiUser,
    FiUsers,
} from 'react-icons/fi';
import { FaRegCopy, FaWhatsapp } from 'react-icons/fa';
import DashboardLayout from '../components/DashboardLayout';
import WorkingShiftAllocation from '../components/WorkingShiftAllocation';
import { apiRequest } from '../utils/api';
import { resolveFileFromCandidates } from '../utils/fileUrl';
import { normalizeTimeForPayload, toTimeInputValue } from '../utils/time';
import { buildServiceHistoryTimeline } from '../utils/serviceHistory';
import confirmIllustration from '../assets/images/employees/confirm.jpg';
import './PendingEmployeeApprovalView.css';
import useOutletSearch from '../hooks/useOutletSearch';
import { BANK_DETAILS_REQUIRED } from '../config/hrmsConfig';

const REQUIRED_FIELD_SECTION_MAP = {
    name: 'personal',
    dob: 'personal',
    gender: 'personal',
    maritalStatus: 'personal',
    fatherName: 'personal',
    spouseName: 'personal',
    phone: 'personal',
    aadhar: 'personal',
    email: 'personal',
    currentAddress: 'personal',
    permanentAddress: 'personal',
    companyId: 'personal',
    accountNumber: 'bank',
    reAccountNumber: 'bank',
    bankName: 'bank',
    ifscCode: 'bank',
};

const REQUIRED_FIELD_MESSAGES = {
    name: 'Employee name is required.',
    dob: 'Date of birth is required.',
    gender: 'Gender is required.',
    maritalStatus: 'Marital status is required.',
    fatherName: "Father's name is required.",
    spouseName: 'Spouse name is required.',
    phone: 'Phone number is required.',
    aadhar: 'Aadhaar number is required.',
    email: 'Email is required.',
    currentAddress: 'Current address is required.',
    permanentAddress: 'Permanent address is required.',
    companyId: 'Company selection is required.',
    accountNumber: 'Account number is required.',
    reAccountNumber: 'Please re-enter the account number.',
    bankName: 'Bank name is required.',
    ifscCode: 'IFSC code is required.',
};

const IMAGE_EXTENSIONS = new Set([
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'webp',
    'svg',
    'heic',
    'heif',
]);

const PDF_EXTENSIONS = new Set(['pdf']);

const extractFileExtension = (value) => {
    if (!value) {
        return '';
    }

    const sanitized = String(value).split('?')[0];
    const match = sanitized.match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toLowerCase() : '';
};

const isImageLike = (fileName, mimeType) => {
    if (mimeType && String(mimeType).toLowerCase().startsWith('image/')) {
        return true;
    }

    const extension = extractFileExtension(fileName);
    return IMAGE_EXTENSIONS.has(extension);
};

const isPdfLike = (fileName, mimeType) => {
    if (mimeType && String(mimeType).toLowerCase() === 'application/pdf') {
        return true;
    }

    const extension = extractFileExtension(fileName);
    return PDF_EXTENSIONS.has(extension);
};

const SectionCardHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="approval-card-header">
        {Icon ? (
            <span className="approval-card-icon" aria-hidden="true">
                <Icon size={20} />
            </span>
        ) : null}
        <div className="approval-card-header-text">
            <h6 className="approval-card-title mb-1">{title}</h6>
            {subtitle ? <p className="approval-card-subtitle mb-0">{subtitle}</p> : null}
        </div>
    </div>
);

const extractMissingFields = (message) => {
    if (!message) {
        return [];
    }

    const match = message.match(/Missing required fields:\s*(.+)$/i);
    if (!match) {
        return [];
    }

    return match[1]
        .split(',')
        .map((field) => field.trim())
        .filter(Boolean);
};

const firstNonEmpty = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && String(item).trim() !== '',
    );
    return value === undefined ? '' : value;
};

const createInitialFormState = () => ({
    personal: {
        name: '',
        dob: '',
        gender: '',
        maritalStatus: '',
        fatherName: '',
        spouseName: '',
        phone: '',
        aadhar: '',
        email: '',
        currentAddress: '',
        permanentAddress: '',
        sameAsCurrentAddress: false,
        outlet: '',
        companyId: '',
        companyName: '',
        tempId: '',
        permId: '',
        showId: '',
        status: 'pending',
        inactiveReason: 'relieving',
        inactiveNotes: '',
        relievingDate: '',
    },
    pf: {
        pfNumber: '',
        uanNumber: '',
        esiNumber: '',
        insurerName: '',
    },
    bank: {
        accountNumber: '',
        reAccountNumber: '',
        bankName: '',
        ifscCode: '',
        panCard: '',
        drivingLicense: '',
    },
    education: {
        qualifications: [],
    },
    professional: {
        lastCompany: '',
        designation: '',
        fromDate: '',
        toDate: '',
    },
    workingTime: {
        type: '',
        shifts: [],
    },
    family: [],
    documents: [],
});

const createInitialFinalReview = () => ({
    outletName: '',
    outletId: '',
    companyId: '',
    companyName: '',
    dateOfJoining: '',
    pfJoinDate: '',
    salary: '',
    designation: '',
    tempId: '',
    permId: '',
    referredEmployeeId: '',
    insuranceNumber: '',
    insurerName: '',
    employeeStatus: 'active',
    inactiveReason: 'relieving',
    inactiveNotes: '',
    relievingDate: '',
});

const toDisplayDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleDateString();
};

const normalizeStatus = (value) => (value || '').toString().trim().toLowerCase();

const isRegistrationComplete = (employee) => {
    const registrationStatus = normalizeStatus(
        employee?.registrationStatus ||
        employee?.mobileRegistrationStatus ||
        employee?.meta?.mobileRegistrationStatus,
    );

    // ✅ If registrationStatus missing but employee is active/approved, treat as complete
    const status = normalizeStatus(employee?.status || employee?.approvalStatus);
    if (!registrationStatus) {
        if (['active', 'approved', 'verified'].includes(status)) return true;
        if (employee?.approvedAt) return true;
        return false;
    }

    const allowedStatuses = new Set(['approved', 'active', 'verified', 'completed', 'complete']);
    return allowedStatuses.has(registrationStatus);
};

const isApprovedEmployee = (employee) => {
    const approvalStatus = normalizeStatus(employee?.approvalStatus || employee?.status);
    return isRegistrationComplete(employee) && ['approved', 'active', 'verified'].includes(approvalStatus);
};

// ✅ FIX: safe date -> input (no timezone shifting for YYYY-MM-DD)
const toDateInputValue = (value) => {
    if (!value) {
        return '';
    }

    // if already YYYY-MM-DD, return as-is
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
        return value.trim();
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
            return value.slice(0, 10);
        }
        return '';
    }

    return date.toISOString().slice(0, 10);
};

const formatCurrencyValue = (value) => {
    if (value === undefined || value === null || value === '') {
        return '—';
    }

    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
        return String(value);
    }

    return `₹${numeric.toLocaleString('en-IN', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    })}`;
};

const mapShiftsToState = (entries = []) => {
    if (!Array.isArray(entries)) {
        return [];
    }

    return entries.map((entry, index) => ({
        id: entry?.id ?? `shift-${index}`,
        shiftId: entry?.shiftId === null || entry?.shiftId === undefined ? '' : String(entry.shiftId),
        timeFrom: toTimeInputValue(entry?.timeFrom ?? entry?.shiftInTime),
        timeTo: toTimeInputValue(entry?.timeTo ?? entry?.shiftOutTime),
        isPrimary: Boolean(entry?.isPrimary ?? index === 0),
        displayOrder: Number.isFinite(Number(entry?.displayOrder)) ? Number(entry.displayOrder) : index,
    }));
};

const normalizeShiftsForPayload = (entries = []) => {
    if (!Array.isArray(entries)) {
        return [];
    }

    const sanitized = entries
        .map((entry, index) => {
            if (!entry) {
                return null;
            }

            const shiftId = entry.shiftId && String(entry.shiftId).trim() !== '' ? String(entry.shiftId) : '';
            const timeFrom = normalizeTimeForPayload(entry.timeFrom);
            const timeTo = normalizeTimeForPayload(entry.timeTo);

            if (!shiftId && !timeFrom && !timeTo) {
                return null;
            }

            return {
                id: entry.id,
                shiftId,
                timeFrom,
                timeTo,
                isPrimary: Boolean(entry.isPrimary),
                displayOrder: Number.isFinite(Number(entry.displayOrder)) ? Number(entry.displayOrder) : index,
            };
        })
        .filter(Boolean);

    if (sanitized.length === 0) {
        return [];
    }

    const primaryIndex = sanitized.findIndex((entry) => entry.isPrimary);
    return sanitized.map((entry, index) => ({
        ...entry,
        displayOrder: index,
        isPrimary: primaryIndex === -1 ? index === 0 : index === primaryIndex,
    }));
};

const buildWorkingTimePayload = (workingTime = {}) => {
    const source = typeof workingTime === 'object' && workingTime !== null ? workingTime : {};
    const normalizedShifts = normalizeShiftsForPayload(source.shifts);
    const primaryShift = normalizedShifts.find((shift) => shift.isPrimary) ?? normalizedShifts[0] ?? null;

    return {
        ...source,
        shifts: normalizedShifts,
        timeFrom: normalizeTimeForPayload(primaryShift?.timeFrom ?? source.timeFrom),
        timeTo: normalizeTimeForPayload(primaryShift?.timeTo ?? source.timeTo),
    };
};

const normalizeDesignationId = (value) => {
    if (value === undefined || value === null) {
        return '';
    }

    const trimmed = String(value).trim();
    return trimmed ? trimmed : '';
};

const resolveEmployeeDateOfJoining = (employee = {}) =>
    toDateInputValue(
        employee?.date_of_join ??
        employee?.dateOfJoin ??
        employee?.dateOfJoining ??
        employee?.joiningDate ??
        '',
    );

const resolveEmployeeDesignationId = (employee = {}) =>
    normalizeDesignationId(
        employee?.designation_id ??
        employee?.designationId ??
        employee?.designation ??
        '',
    );

const PendingEmployeeApprovalView = ({ source = 'approval' }) => {
    const navigate = useNavigate();
    const { id, employeeId } = useParams();
    const isEmployeeMasterView = useMemo(() => source === 'employee' || Boolean(employeeId), [employeeId, source]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [request, setRequest] = useState(null);
    const [formData, setFormData] = useState(createInitialFormState);
    const [finalReview, setFinalReview] = useState(createInitialFinalReview);
    const [remarks, setRemarks] = useState('');
    const [isDateOfJoiningManual, setIsDateOfJoiningManual] = useState(false);
    const [isDesignationManual, setIsDesignationManual] = useState(false);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [employeeDetailsMap, setEmployeeDetailsMap] = useState({});
    const [employeeRecord, setEmployeeRecord] = useState(null);
    const [shiftOptions, setShiftOptions] = useState([]);
    const [shiftOptionsLoading, setShiftOptionsLoading] = useState(false);
    const [shiftOptionsError, setShiftOptionsError] = useState(null);
    const [designationOptions, setDesignationOptions] = useState([]);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [approvedEmployeeSummary, setApprovedEmployeeSummary] = useState(null);
    const [isSharingApprovalImage, setIsSharingApprovalImage] = useState(false);
    const [isCopyingApprovalImage, setIsCopyingApprovalImage] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [latestIds, setLatestIds] = useState({
        latestTempId: null,
        latestPermanentId: null,
        nextTempId: 30001,
        nextPermanentId: 10001,
    });
    const [documentPreview, setDocumentPreview] = useState(null);
    const fieldRefs = useRef({});
    const previewResourceRef = useRef(null);
    const approvalSuccessCardRef = useRef(null);
    const [isOutletFocused, setIsOutletFocused] = useState(false);
    const [pfJoinDate, setPfJoinDate] = useState('');
    const [isPfJoinLoading, setIsPfJoinLoading] = useState(false);
    const [pfJoinError, setPfJoinError] = useState('');

    const {
        searchTerm: outletSearchTerm,
        setSearchTerm: setOutletSearchTerm,
        options: outletOptions,
        loading: isOutletSearching,
        error: outletSearchError,
        search: searchOutlets,
        clearOptions: clearOutletOptions,
    } = useOutletSearch({ navigate, companyId: finalReview.companyId || formData.personal.companyId || '' });

    const targetEmployeeId = useMemo(() => request?.employeeId || employeeId || '', [employeeId, request?.employeeId]);

    const clearFieldErrors = useCallback(() => {
        setFieldErrors({});
    }, []);

    const applyComplianceErrors = useCallback((errors = {}) => {
        const trackedKeys = [
            'pf.uanNumber',
            'pf.esiNumber',
            'bank.accountNumber',
            'bank.reAccountNumber',
            'bank.bankName',
            'bank.ifscCode',
        ];

        setFieldErrors((previous) => {
            const next = { ...previous };
            trackedKeys.forEach((key) => {
                if (errors[key]) {
                    next[key] = errors[key];
                } else {
                    delete next[key];
                }
            });
            return next;
        });
    }, []);

    const hasFieldError = useCallback((section, field) => Boolean(fieldErrors[`${section}.${field}`]), [fieldErrors]);

    const validateComplianceFields = useCallback(() => {
        const errors = {};

        const trimmedUan = (formData.pf.uanNumber || '').trim();
        if (trimmedUan && !/^\d{12}$/.test(trimmedUan)) {
            errors['pf.uanNumber'] = 'UAN number must be exactly 12 digits.';
        }

        const trimmedEsi = (formData.pf.esiNumber || '').trim();
        if (trimmedEsi && !/^\d{10}$/.test(trimmedEsi)) {
            errors['pf.esiNumber'] = 'ESI number must be exactly 10 digits.';
        }

        const accountNumber = (formData.bank.accountNumber || '').trim();
        const reAccountNumber = (formData.bank.reAccountNumber || '').trim();
        const bankName = (formData.bank.bankName || '').trim();
        const ifscCode = (formData.bank.ifscCode || '').trim();

        if (BANK_DETAILS_REQUIRED) {
            if (!accountNumber) errors['bank.accountNumber'] = REQUIRED_FIELD_MESSAGES.accountNumber;
            if (!reAccountNumber) errors['bank.reAccountNumber'] = REQUIRED_FIELD_MESSAGES.reAccountNumber;
            if (!bankName) errors['bank.bankName'] = REQUIRED_FIELD_MESSAGES.bankName;
            if (!ifscCode) errors['bank.ifscCode'] = REQUIRED_FIELD_MESSAGES.ifscCode;
        }

        if (accountNumber && !/^\d+$/.test(accountNumber)) {
            errors['bank.accountNumber'] = 'Account number must contain digits only.';
        }

        if (reAccountNumber && !/^\d+$/.test(reAccountNumber)) {
            errors['bank.reAccountNumber'] = 'Re-entered account number must contain digits only.';
        }

        if (accountNumber && !reAccountNumber) {
            errors['bank.reAccountNumber'] = 'Please re-enter the account number.';
        }

        if (
            accountNumber &&
            reAccountNumber &&
            /^\d+$/.test(accountNumber) &&
            /^\d+$/.test(reAccountNumber) &&
            accountNumber !== reAccountNumber
        ) {
            errors['bank.reAccountNumber'] = 'Account numbers must match.';
        }

        applyComplianceErrors(errors);

        if (Object.keys(errors).length > 0) {
            setStatusMessage({
                type: 'danger',
                message: 'Please correct the highlighted PF & bank fields.',
            });
            return false;
        }

        return true;
    }, [
        applyComplianceErrors,
        formData.bank.accountNumber,
        formData.bank.bankName,
        formData.bank.ifscCode,
        formData.bank.reAccountNumber,
        formData.pf.esiNumber,
        formData.pf.uanNumber,
    ]);

    const highlightMissingFields = useCallback((missingFields) => {
        const recognizedFields = Array.isArray(missingFields)
            ? missingFields.filter((field) => REQUIRED_FIELD_SECTION_MAP[field])
            : [];

        if (!recognizedFields.length) {
            return false;
        }

        setFieldErrors(
            recognizedFields.reduce((accumulator, field) => {
                const section = REQUIRED_FIELD_SECTION_MAP[field];
                if (section) {
                    accumulator[`${section}.${field}`] = true;
                }
                return accumulator;
            }, {}),
        );

        const schedule =
            typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
                ? window.requestAnimationFrame.bind(window)
                : (callback) => setTimeout(callback, 0);

        schedule(() => {
            let scrolled = false;
            recognizedFields.forEach((field) => {
                if (scrolled) return;

                const section = REQUIRED_FIELD_SECTION_MAP[field];
                if (!section) return;

                const key = `${section}.${field}`;
                const element = fieldRefs.current[key];
                if (element && typeof element.scrollIntoView === 'function') {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    if (typeof element.focus === 'function') {
                        element.focus({ preventScroll: true });
                    }
                    scrolled = true;
                }
            });

            if (!scrolled && typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        });

        return true;
    }, []);

    const isRequestPending = useMemo(
        () => isEmployeeMasterView || (request?.status || '').toLowerCase() === 'pending',
        [isEmployeeMasterView, request?.status],
    );

    const getDocumentLink = useCallback((document) => {
        if (!document) {
            return null;
        }

        const pathCandidate =
            document.fileUrl ??
            document.documentUrl ??
            document.previewUrl ??
            document.downloadUrl ??
            document.url ??
            document.documentPath ??
            document.filePath ??
            document.path ??
            null;

        const documentFileName = document.fileName ?? null;
        const combineWithFileName = (basePath) => {
            if (!basePath) return null;
            if (!documentFileName) return basePath;

            const sanitizedFileName = String(documentFileName).replace(/^\/+/, '');
            const lowerBase = basePath.toLowerCase();
            const lowerFileName = sanitizedFileName.toLowerCase();

            if (lowerBase.includes(lowerFileName)) return basePath;

            const separator = basePath.endsWith('/') || sanitizedFileName.startsWith('/') ? '' : '/';
            return `${basePath}${separator}${sanitizedFileName}`;
        };

        let resolvedPath = combineWithFileName(pathCandidate);

        if (!resolvedPath && document.documentPath && documentFileName) {
            resolvedPath = combineWithFileName(document.documentPath);
        }

        if (!resolvedPath && document.filePath && documentFileName) {
            resolvedPath = combineWithFileName(document.filePath);
        }

        if (!resolvedPath) return null;

        return resolveFileFromCandidates(resolvedPath);
    }, []);

    const resolveDocumentResource = useCallback(
        (docItem) => {
            if (!docItem) return null;

            if (docItem.file instanceof Blob) {
                const objectUrl = URL.createObjectURL(docItem.file);
                return {
                    url: objectUrl,
                    fileName: docItem.fileName ?? docItem.documentType ?? 'document',
                    mimeType: docItem.file.type ?? docItem.mimeType ?? '',
                    isObjectUrl: true,
                };
            }

            const directLink = getDocumentLink(docItem);
            if (!directLink) return null;

            return {
                url: directLink,
                fileName: docItem.fileName ?? docItem.documentType ?? '',
                mimeType: docItem.mimeType ?? '',
                isObjectUrl: false,
            };
        },
        [getDocumentLink],
    );

    const handleViewDocument = useCallback(
        (docItem) => {
            if (!docItem) return;

            const resource = resolveDocumentResource(docItem);
            if (!resource?.url) {
                setStatusMessage({
                    type: 'warning',
                    message: 'Document preview is not available for this file.',
                });
                return;
            }

            const candidateName = resource.fileName || docItem.fileName || docItem.documentType || '';
            const mimeType =
                resource.mimeType || docItem.mimeType || (docItem.file instanceof Blob ? docItem.file.type : '');
            const isImage = isImageLike(candidateName || resource.url, mimeType);

            if (isImage) {
                if (previewResourceRef.current?.isObjectUrl && previewResourceRef.current?.url) {
                    URL.revokeObjectURL(previewResourceRef.current.url);
                }

                previewResourceRef.current = resource;
                setDocumentPreview({
                    url: resource.url,
                    fileName: candidateName || 'document',
                    title: docItem.documentType || candidateName || 'Document Preview',
                    mimeType,
                });
                return;
            }

            window.open(resource.url, '_blank', 'noopener,noreferrer');
            if (resource.isObjectUrl) {
                setTimeout(() => {
                    URL.revokeObjectURL(resource.url);
                }, 60_000);
            }
        },
        [resolveDocumentResource],
    );

    const handleDownloadDocument = useCallback(
        (docItem) => {
            if (!docItem) return;

            const resource = resolveDocumentResource(docItem);
            if (!resource?.url) {
                setStatusMessage({
                    type: 'warning',
                    message: 'Download is not available for this document.',
                });
                return;
            }

            const anchor = window.document.createElement('a');
            anchor.href = resource.url;
            anchor.download = resource.fileName || 'document';
            anchor.rel = 'noopener noreferrer';
            window.document.body.appendChild(anchor);
            anchor.click();
            window.document.body.removeChild(anchor);

            if (resource.isObjectUrl) {
                setTimeout(() => {
                    URL.revokeObjectURL(resource.url);
                }, 60_000);
            }
        },
        [resolveDocumentResource],
    );

    const closeDocumentPreview = useCallback(() => {
        if (previewResourceRef.current?.isObjectUrl && previewResourceRef.current?.url) {
            URL.revokeObjectURL(previewResourceRef.current.url);
        }
        previewResourceRef.current = null;
        setDocumentPreview(null);
    }, []);

    const handleRemoveDocument = useCallback(
        (documentIndex) => {
            if (!isRequestPending) return;

            setFormData((prev) => ({
                ...prev,
                documents: prev.documents.filter((_, index) => index !== documentIndex),
            }));
        },
        [isRequestPending],
    );

    const canViewDocument = useCallback(
        (document) => {
            if (!document) return false;
            if (document.file instanceof Blob) return true;
            return Boolean(getDocumentLink(document));
        },
        [getDocumentLink],
    );

    const formatQualificationValue = useCallback((value) => {
        if (value === null || value === undefined) return '—';
        if (typeof value === 'string' && value.trim() === '') return '—';
        return String(value);
    }, []);

    const qualificationEntries = useMemo(() => {
        const list = Array.isArray(formData.education?.qualifications) ? formData.education.qualifications : [];

        return list.map((entry, index) => {
            const rawTitle = entry.qualificationTitle || entry.qualificationName || entry.qualificationType || '';
            const trimmedTitle = String(rawTitle || '').trim();
            const displayTitle = trimmedTitle ? `${index + 1}. ${trimmedTitle.toUpperCase()}` : `Qualification ${index + 1}`;

            const qualificationTypeValue = entry.qualificationType ?? entry.qualificationTitle ?? entry.qualificationName ?? '';
            const courseValue = entry.courseName ?? '';
            const qualificationParts = [qualificationTypeValue, courseValue]
                .map((value) => (value ? String(value).trim() : ''))
                .filter(Boolean);

            const qualificationValue =
                qualificationParts.length > 1 ? qualificationParts.join(' - ') : qualificationParts[0] || qualificationTypeValue || courseValue;

            const documentFileName = entry.fileName ?? entry.documentName ?? entry.originalFileName ?? entry.file ?? '';
            const documentUrl = getDocumentLink({ ...entry, fileName: documentFileName });
            const documentDescriptor = documentUrl
                ? {
                    fileUrl: documentUrl,
                    fileName: documentFileName,
                    mimeType: entry.mimeType ?? entry.fileType ?? '',
                    documentType: rawTitle || entry.courseName || 'Qualification Document',
                }
                : null;

            return {
                id: entry.id || entry.localId || `qualification-${index}`,
                title: displayTitle,
                fields: [
                    { label: 'Qualification / Course', value: formatQualificationValue(qualificationValue) },
                    { label: 'Institution', value: formatQualificationValue(entry.institutionName) },
                    { label: 'Year of Passing', value: formatQualificationValue(entry.yearOfPassing) },
                    { label: 'Grade / Percentage', value: formatQualificationValue(entry.gradeOrPercentage) },
                    {
                        label: 'Document',
                        value: formatQualificationValue(
                            documentFileName ||
                            (entry.documentPath ? String(entry.documentPath).split('/').filter(Boolean).pop() : ''),
                        ),
                        documentUrl,
                    },
                ],
                documentDescriptor,
            };
        });
    }, [formData.education?.qualifications, formatQualificationValue, getDocumentLink]);

    const hasQualificationEntries = qualificationEntries.length > 0;

    const designationNameOptions = useMemo(
        () =>
            designationOptions.map((option) => ({
                value: option.label,
                label: option.label,
            })),
        [designationOptions],
    );

    const resolvedProfessionalDesignationOptions = useMemo(() => {
        const baseOptions = designationNameOptions;
        const existingValues = new Set(baseOptions.map((option) => option.value));
        const additionalValues = [formData.professional.designation]
            .map((value) => (value ? String(value).trim() : ''))
            .filter((value) => value && !existingValues.has(value));

        if (!additionalValues.length) return baseOptions;

        const additionalOptions = additionalValues.map((value) => ({ value, label: value }));
        return [...baseOptions, ...additionalOptions];
    }, [designationNameOptions, formData.professional.designation]);

    const resolvedFinalReviewDesignationOptions = useMemo(() => {
        const baseOptions = designationOptions;
        const existingValues = new Set(baseOptions.map((option) => option.value));
        const additionalValues = [finalReview.designation]
            .map((value) => (value ? String(value).trim() : ''))
            .filter((value) => value && !existingValues.has(value));

        if (!additionalValues.length) return baseOptions;

        const additionalOptions = additionalValues.map((value) => ({ value, label: value }));
        return [...baseOptions, ...additionalOptions];
    }, [designationOptions, finalReview.designation]);

    const finalReviewDesignationLabel = useMemo(() => {
        const designationValue = finalReview.designation ? String(finalReview.designation).trim() : '';
        if (!designationValue) return '';

        const matched = designationOptions.find((option) => option.value === designationValue);
        if (matched) return matched.label;

        const matchedLabel = designationOptions.find(
            (option) => option.label?.toLowerCase() === designationValue.toLowerCase(),
        );
        return matchedLabel?.label ?? designationValue;
    }, [designationOptions, finalReview.designation]);

    const showSpouseNameField = useMemo(() => {
        const gender = String(formData.personal.gender || '').trim().toLowerCase();
        const maritalStatus = String(formData.personal.maritalStatus || '').trim().toLowerCase();
        return gender === 'female' && maritalStatus === 'married';
    }, [formData.personal.gender, formData.personal.maritalStatus]);

    const applyOptionalDigitValidation = useCallback((fieldKey, value, expectedLength, message) => {
        setFieldErrors((prev) => {
            const next = { ...prev };
            if (value && value.length !== expectedLength) {
                next[fieldKey] = message;
            } else {
                delete next[fieldKey];
            }
            return next;
        });
    }, []);

    const updateSectionField = useCallback(
        (section, field, value) => {
            let nextValue = value;
            const fieldKey = `${section}.${field}`;

            if (fieldKey === 'pf.uanNumber') {
                nextValue = (value || '').replace(/\D/g, '').slice(0, 12);
                applyOptionalDigitValidation(fieldKey, nextValue, 12, 'UAN number must be exactly 12 digits.');
            } else if (fieldKey === 'pf.esiNumber') {
                nextValue = (value || '').replace(/\D/g, '').slice(0, 10);
                applyOptionalDigitValidation(fieldKey, nextValue, 10, 'ESI number must be exactly 10 digits.');
            }

            setFormData((prev) => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: nextValue,
                },
            }));

            if (fieldKey !== 'pf.uanNumber' && fieldKey !== 'pf.esiNumber') {
                setFieldErrors((prev) => {
                    if (!prev[fieldKey]) return prev;
                    const next = { ...prev };
                    delete next[fieldKey];
                    return next;
                });
            }
        },
        [applyOptionalDigitValidation],
    );

    const handleWorkingShiftsChange = useCallback((entries) => {
        setFormData((prev) => ({
            ...prev,
            workingTime: {
                ...prev.workingTime,
                shifts: entries,
            },
        }));
        setFieldErrors((prev) => {
            if (!prev['workingTime.shifts']) return prev;
            const next = { ...prev };
            delete next['workingTime.shifts'];
            return next;
        });
    }, []);

    // ✅ FIX: remove wrong mapping dateOfJoining -> professional.fromDate
    const updateFinalReview = useCallback(
        (field, value) => {
            setFinalReview((prev) => ({
                ...prev,
                [field]: value,
                ...(field === 'companyId' ? { outletId: '', outletName: '', designation: '' } : {}),
            }));

            if (field === 'tempId') updateSectionField('personal', 'tempId', value);
            if (field === 'permId') updateSectionField('personal', 'permId', value);
            if (field === 'companyId') {
                updateSectionField('personal', 'companyId', value);
                updateSectionField('personal', 'outlet', '');
                updateSectionField('workingTime', 'shifts', []);
                setOutletSearchTerm('');
                clearOutletOptions();
            }
            if (field === 'companyName') updateSectionField('personal', 'companyName', value);
            if (field === 'employeeStatus') updateSectionField('personal', 'status', value);
            if (field === 'insurerName') updateSectionField('pf', 'insurerName', value);

            // ❌ removed:
            // if (field === 'dateOfJoining') updateSectionField('professional', 'fromDate', value);
        },
        [clearOutletOptions, setOutletSearchTerm, updateSectionField],
    );

    const fetchCompanies = useCallback(async () => {
        try {
            const response = await apiRequest('/api/admin/companies', {}, navigate);
            const options = Array.isArray(response.data)
                ? response.data
                    .filter((company) => (company.status ? company.status === 'active' : true))
                    .map((company) => ({ value: String(company.id), label: company.name }))
                : [];
            setCompanyOptions(options);
        } catch (fetchError) {
            console.error('Failed to load companies', fetchError);
            setCompanyOptions([]);
        }
    }, [navigate]);

    const fetchEmployees = useCallback(async () => {
        try {
            const response = await apiRequest('/api/admin/employees?status=active', {}, navigate);
            const list = Array.isArray(response.data?.employees)
                ? response.data.employees
                : Array.isArray(response.data)
                    ? response.data
                    : [];
            const approvedEmployees = list.filter((employee) => isApprovedEmployee(employee));
            const lookup = approvedEmployees.reduce((accumulator, employee) => {
                if (employee && employee.id != null) {
                    accumulator[String(employee.id)] = employee;
                }
                return accumulator;
            }, {});
            const options = approvedEmployees.map((employee) => {
                const identifier = employee.permId || employee.tempId || '';
                const name = employee.name || 'Employee';
                return { value: String(employee.id), label: identifier ? `${name} (${identifier})` : name };
            });
            setEmployeeDetailsMap(lookup);
            setEmployeeOptions(options);
        } catch (fetchError) {
            console.error('Failed to load employees', fetchError);
            setEmployeeOptions([]);
            setEmployeeDetailsMap({});
        }
    }, [navigate]);

    const fetchDesignations = useCallback(async (companyId = '') => {
        try {
            const params = new URLSearchParams();
            if (companyId) {
                params.set('companyId', companyId);
            }
            const endpoint = params.toString()
                ? `/api/admin/settings/designations?${params.toString()}`
                : '/api/admin/settings/designations';
            const response = await apiRequest(endpoint, {}, navigate);
            const rows = Array.isArray(response.data) ? response.data : [];
            const options = rows
                .filter((designation) => (designation.status ? designation.status.toLowerCase() !== 'inactive' : true))
                .map((designation) => ({
                    value: designation.id ? String(designation.id) : '',
                    label: designation.designation || designation.name || '',
                }))
                .filter((option) => option.value !== '' && option.label !== '');

            const seenValues = new Set();
            const uniqueOptions = options.filter((option) => {
                if (seenValues.has(option.value)) return false;
                seenValues.add(option.value);
                return true;
            });
            setDesignationOptions(uniqueOptions);
        } catch (fetchError) {
            console.error('Failed to load designations', fetchError);
            setDesignationOptions([]);
        }
    }, [navigate]);

    useEffect(() => {
        if (designationOptions.length === 0) return;

        setFinalReview((prev) => {
            const currentValue = normalizeDesignationId(prev.designation);
            if (!currentValue) return prev;

            const matchById = designationOptions.find((option) => option.value === currentValue);
            if (matchById) return prev.designation === currentValue ? prev : { ...prev, designation: currentValue };

            const matchByName = designationOptions.find(
                (option) => option.label?.toLowerCase() === currentValue.toLowerCase(),
            );
            if (matchByName && matchByName.value !== currentValue) {
                return { ...prev, designation: matchByName.value };
            }

            return prev;
        });
    }, [designationOptions]);

    const fetchShiftOptions = useCallback(async (companyId = '') => {
        try {
            setShiftOptionsLoading(true);
            setShiftOptionsError(null);
            if (!companyId) {
                setShiftOptions([]);
                return;
            }
            const params = new URLSearchParams({ companyId: String(companyId) });
            const response = await apiRequest(`/api/admin/settings/shifts?${params.toString()}`, {}, navigate);
            const rows = Array.isArray(response.data) ? response.data : [];
            const options = rows
                .filter((shift) => String(shift?.status ?? '').toLowerCase() !== 'inactive')
                .map((shift) => ({
                    value: String(shift.id),
                    label: `${shift.shiftName ?? 'Shift'}${shift.inTime || shift.outTime
                        ? ` (${toTimeInputValue(shift.inTime) || '—'} - ${toTimeInputValue(shift.outTime) || '—'})`
                        : ''
                        }`,
                    inTime: shift.inTime ?? null,
                    outTime: shift.outTime ?? null,
                }));
            setShiftOptions(options);
        } catch (fetchError) {
            console.error('Failed to load shift options', fetchError);
            setShiftOptions([]);
            setShiftOptionsError(fetchError.message || 'Failed to load shifts.');
        } finally {
            setShiftOptionsLoading(false);
        }
    }, [navigate]);

    const fetchPfJoinDate = useCallback(async () => {
        if (!targetEmployeeId) {
            setPfJoinError('No linked employee available to fetch PF join date.');
            return;
        }

        try {
            setIsPfJoinLoading(true);
            setPfJoinError('');
            const response = await apiRequest(`/api/admin/employees/${targetEmployeeId}/pf-join-date`, {}, navigate);
            const nextDate = toDateInputValue(response.data?.pfJoinDate);
            if (nextDate) {
                setPfJoinDate(nextDate);
            } else {
                setPfJoinDate('');
                setPfJoinError('No PF join date recorded for this employee.');
            }
        } catch (fetchError) {
            console.error('Failed to load PF join date', fetchError);
            setPfJoinError(fetchError.message || 'Failed to load PF join date.');
        } finally {
            setIsPfJoinLoading(false);
        }
    }, [navigate, targetEmployeeId]);

    const fetchLatestIds = useCallback(async (companyId = '') => {
        try {
            const params = new URLSearchParams();
            if (companyId) params.set('companyId', companyId);
            const queryString = params.toString();
            const response = await apiRequest(
                `/api/admin/employees/id-sequence${queryString ? `?${queryString}` : ''}`,
                {},
                navigate,
            );
            const data = response.data ?? {};
            setLatestIds({
                latestTempId: data.latestTempId ?? null,
                latestPermanentId: data.latestPermanentId ?? null,
                nextTempId: data.nextTempId ?? 30001,
                nextPermanentId: data.nextPermanentId ?? 10001,
            });
        } catch (fetchError) {
            console.error('Failed to load latest employee identifiers', fetchError);
            setLatestIds((prev) => ({ ...prev }));
        }
    }, [navigate]);

    const mapRequestToState = useCallback(
        (approvalRequest) => {
            setPfJoinDate('');
            setPfJoinError('');

            if (!approvalRequest) {
                setFormData(createInitialFormState());
                setFinalReview(createInitialFinalReview());
                clearFieldErrors();
                return;
            }

            const personal = approvalRequest.personal ?? {};
            const pf = approvalRequest.pf ?? {};
            const bank = approvalRequest.bank ?? approvalRequest.employee?.bank ?? approvalRequest.employee?.bankDetails ?? {};
            const education = approvalRequest.education ?? approvalRequest.employee?.education ?? {};
            const educationQualifications = Array.isArray(education?.qualifications)
                ? education.qualifications
                : Array.isArray(education)
                    ? education
                    : [];
            const professional = approvalRequest.professional ?? {};
            const workingTime = approvalRequest.workingTime ?? {};
            const family = Array.isArray(approvalRequest.family?.members)
                ? approvalRequest.family.members
                : Array.isArray(approvalRequest.family)
                    ? approvalRequest.family
                    : [];
            const documents = Array.isArray(approvalRequest.documents) ? approvalRequest.documents : [];
            const review = approvalRequest.finalReview ?? {};
            const employee = approvalRequest.employee ?? {};

            const resolvedStatus = (personal.status ?? review.employeeStatus ?? 'pending').toLowerCase();
            const normalizedResolvedStatus = resolvedStatus === 'inactive' ? 'inactive' : 'active';
            const resolvedInactiveReason =
                personal.inactiveReason ?? review.inactiveReason ?? (resolvedStatus === 'inactive' ? 'relieving' : '');
            const resolvedInactiveNotes = personal.inactiveNotes ?? review.inactiveNotes ?? '';
            const resolvedRelievingDate = personal.relievingDate ?? review.relievingDate ?? '';

            const resolvedTempId = (() => {
                const value = review.tempId ?? review.tempEmployeeId ?? personal.tempId ?? personal.tempEmployeeId ?? '';
                return value === null || value === undefined ? '' : String(value);
            })();

            const resolvedPermId = (() => {
                const value = review.permId ?? review.permanentEmployeeId ?? personal.permId ?? personal.permanentEmployeeId ?? '';
                return value === null || value === undefined ? '' : String(value);
            })();

            const outletValue =
                personal.outlet ?? personal.outletId ?? (approvalRequest.outlet?.id ? String(approvalRequest.outlet.id) : '');

            const employeeDateOfJoining = resolveEmployeeDateOfJoining(employee);
            const reviewDateOfJoining = toDateInputValue(review.dateOfJoining);

            const companyIdValue = (() => {
                const value = review.companyId ?? personal.companyId ?? approvalRequest.company?.id ?? approvalRequest.employee?.company?.id ?? '';
                return value === null || value === undefined ? '' : String(value);
            })();

            const companyNameValue =
                review.companyName ?? personal.companyName ?? approvalRequest.company?.name ?? approvalRequest.employee?.company?.name ?? '';

            setFormData({
                personal: {
                    name: personal.name ?? '',
                    dob: personal.dob ?? '',
                    gender: personal.gender ?? '',
                    maritalStatus: personal.maritalStatus ?? '',
                    fatherName: firstNonEmpty(
                        personal.fatherName,
                        approvalRequest.employee?.personal?.fatherName,
                        approvalRequest.employee?.fatherName,
                    ),
                    spouseName: firstNonEmpty(
                        personal.spouseName,
                        approvalRequest.employee?.personal?.spouseName,
                        approvalRequest.employee?.spouseName,
                    ),
                    phone: personal.phone ?? '',
                    aadhar: personal.aadhar ?? approvalRequest.aadhar ?? '',
                    email: personal.email ?? '',
                    currentAddress: personal.currentAddress ?? '',
                    permanentAddress: personal.permanentAddress ?? '',
                    sameAsCurrentAddress: Boolean(personal.sameAsCurrentAddress),
                    outlet: outletValue ? String(outletValue) : '',
                    companyId: companyIdValue,
                    companyName: companyNameValue,
                    tempId: resolvedTempId,
                    permId: resolvedPermId,
                    showId:
                        personal.showId ??
                        personal.showEmployeeId ??
                        personal.employeeShowId ??
                        personal.show_employee_id ??
                        '',
                    status: personal.status ?? review.employeeStatus ?? normalizedResolvedStatus,
                    inactiveReason: resolvedInactiveReason,
                    inactiveNotes: resolvedInactiveNotes,
                    relievingDate: resolvedRelievingDate,
                },
                pf: {
                    pfNumber: pf.pfNumber ?? '',
                    uanNumber: pf.uanNumber ?? '',
                    esiNumber: pf.esiNumber ?? '',
                    insurerName: pf.insurerName ?? review.insurerName ?? '',
                },
                bank: {
                    accountNumber: bank.accountNumber ?? '',
                    reAccountNumber: bank.reAccountNumber ?? bank.accountNumber ?? '',
                    bankName: bank.bankName ?? approvalRequest.employee?.bankName ?? '',
                    ifscCode: bank.ifscCode ?? approvalRequest.employee?.ifscCode ?? '',
                    panCard: bank.panCard ?? '',
                    drivingLicense: bank.drivingLicense ?? '',
                },
                education: {
                    qualifications: educationQualifications,
                },
                professional: {
                    lastCompany: professional.lastCompany ?? '',
                    designation: professional.designation ?? '',
                    fromDate: toDateInputValue(professional.fromDate) ?? '',
                    toDate: toDateInputValue(professional.toDate) ?? '',
                },
                workingTime: {
                    type: workingTime.type ?? '',
                    shifts: mapShiftsToState(workingTime.shifts),
                },
                family,
                documents,
            });

            // ✅ FIX: employee table dateOfJoining wins over review/request (EMPLOYEE FIRST)
            setFinalReview({
                outletName: approvalRequest.outlet?.name ?? review.outletName ?? '',
                outletId: outletValue ? String(outletValue) : '',
                companyId: companyIdValue,
                companyName: companyNameValue,
                dateOfJoining: employeeDateOfJoining || reviewDateOfJoining || '',
                pfJoinDate: review.pfJoinDate ?? '',
                salary: review.salary ?? employee?.salary ?? '',
                designation: normalizeDesignationId(review.designation) || resolveEmployeeDesignationId(employee),
                tempId: resolvedTempId,
                permId: resolvedPermId,
                referredEmployeeId: (() => {
                    const candidate = review.referredEmployeeId ?? employee?.referredEmployeeId ?? employee?.referredEmployee?.id ?? null;
                    return candidate ? String(candidate) : '';
                })(),
                insuranceNumber: review.insuranceNumber ?? '',
                insurerName: review.insurerName ?? pf.insurerName ?? '',
                employeeStatus: review.employeeStatus ?? personal.status ?? normalizedResolvedStatus,
                inactiveReason: review.inactiveReason ?? resolvedInactiveReason,
                inactiveNotes: review.inactiveNotes ?? resolvedInactiveNotes,
                relievingDate: review.relievingDate ?? resolvedRelievingDate,
            });

            setRemarks(approvalRequest.remarks ?? '');
            if (review.pfJoinDate) setPfJoinDate(review.pfJoinDate);

            clearFieldErrors();
        },
        [clearFieldErrors],
    );

    const fetchRequest = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const endpoint = isEmployeeMasterView
                ? `/api/admin/employees/${employeeId}/approval-view`
                : `/api/admin/employee-approvals/${id}`;
            const response = await apiRequest(endpoint, {}, navigate);
            setRequest(response.data);
            mapRequestToState(response.data);
        } catch (fetchError) {
            console.error('Failed to load approval request', fetchError);
            const fallbackMessage = isEmployeeMasterView ? 'Failed to load employee details' : 'Failed to load approval request';
            setError(fetchError.message || fallbackMessage);
        } finally {
            setLoading(false);
        }
    }, [employeeId, id, isEmployeeMasterView, mapRequestToState, navigate]);

    useEffect(() => {
        fetchRequest();
        fetchCompanies();
        fetchEmployees();
    }, [fetchRequest, fetchCompanies, fetchEmployees]);

    useEffect(() => {
        const resolvedCompanyId = finalReview.companyId || formData.personal.companyId || request?.company?.id || '';
        fetchLatestIds(resolvedCompanyId);
    }, [fetchLatestIds, finalReview.companyId, formData.personal.companyId, request?.company?.id]);

    useEffect(() => {
        const resolvedCompanyId = finalReview.companyId || formData.personal.companyId || '';
        fetchDesignations(resolvedCompanyId);
        fetchShiftOptions(resolvedCompanyId);
    }, [fetchDesignations, fetchShiftOptions, finalReview.companyId, formData.personal.companyId]);

    useEffect(() => {
        setShowSuccessModal(false);
        setApprovedEmployeeSummary(null);
    }, [employeeId, id]);

    useEffect(() => {
        if (isOutletFocused) return;

        const selectedId = finalReview.outletId || formData.personal.outlet;
        const selectedName =
            finalReview.outletName ||
            request?.outlet?.branchName ||
            request?.outlet?.name ||
            employeeRecord?.outlet?.branchName ||
            employeeRecord?.outlet?.name ||
            '';

        if (selectedId && selectedName) {
            if (outletSearchTerm !== selectedName) {
                setOutletSearchTerm(selectedName);
            }
            if (finalReview.outletName !== selectedName) {
                updateFinalReview('outletName', selectedName);
            }
            return;
        }

        const fallbackOutletId = employeeRecord?.outlet?.id;
        if (!selectedId && fallbackOutletId != null) {
            const nextId = String(fallbackOutletId);
            updateSectionField('personal', 'outlet', nextId);
            updateFinalReview('outletId', nextId);
            if (selectedName) {
                updateFinalReview('outletName', selectedName);
                setOutletSearchTerm(selectedName);
            }
        }
    }, [
        employeeRecord?.outlet?.branchName,
        employeeRecord?.outlet?.id,
        employeeRecord?.outlet?.name,
        finalReview.outletId,
        finalReview.outletName,
        formData.personal.outlet,
        isOutletFocused,
        outletSearchTerm,
        request?.outlet?.branchName,
        request?.outlet?.name,
        setOutletSearchTerm,
        updateFinalReview,
        updateSectionField,
    ]);

    useEffect(
        () => () => {
            if (previewResourceRef.current?.isObjectUrl && previewResourceRef.current?.url) {
                URL.revokeObjectURL(previewResourceRef.current.url);
            }
        },
        [],
    );

    useEffect(() => {
        if (!request?.employeeId) {
            setEmployeeRecord(null);
            return;
        }

        const loadEmployee = async () => {
            try {
                const response = await apiRequest(`/api/admin/employees/${request.employeeId}`, {}, navigate);
                setEmployeeRecord(response.data);
            } catch (employeeError) {
                console.error('Failed to load employee record', employeeError);
                setEmployeeRecord(null);
            }
        };

        loadEmployee();
    }, [request?.employeeId, navigate]);

    useEffect(() => {
        setIsDateOfJoiningManual(false);
        setIsDesignationManual(false);
    }, [request?.employeeId, employeeId]);

    // ✅ FIX: if employeeRecord has DOJ/designation, override (even if UI already has wrong value)
    useEffect(() => {
        if (!employeeRecord) return;

        const nextDateOfJoining = resolveEmployeeDateOfJoining(employeeRecord);
        if (
            !isDateOfJoiningManual &&
            nextDateOfJoining &&
            nextDateOfJoining !== finalReview.dateOfJoining
        ) {
            updateFinalReview('dateOfJoining', nextDateOfJoining);
        }

        const nextDesignation = resolveEmployeeDesignationId(employeeRecord);
        if (!isDesignationManual && nextDesignation && nextDesignation !== finalReview.designation) {
            updateFinalReview('designation', nextDesignation);
        }
    }, [
        employeeRecord,
        finalReview.dateOfJoining,
        finalReview.designation,
        isDateOfJoiningManual,
        isDesignationManual,
        updateFinalReview,
    ]);

    useEffect(() => {
        if (!finalReview.companyId) return;

        const selected = companyOptions.find((option) => option.value === String(finalReview.companyId));
        if (selected && selected.label !== finalReview.companyName) {
            setFinalReview((prev) => ({ ...prev, companyName: selected.label }));
            updateSectionField('personal', 'companyName', selected.label);
        }
    }, [companyOptions, finalReview.companyId, finalReview.companyName, updateSectionField]);

    const buildPayload = useCallback(
        () => ({
            personal: formData.personal,
            pf: formData.pf,
            bank: formData.bank,
            education: formData.education,
            professional: formData.professional,
            workingTime: buildWorkingTimePayload(formData.workingTime),
            family: { members: formData.family },
            documents: formData.documents,
            finalReview: {
                ...finalReview,
                pfJoinDate,
            },
            remarks,
        }),
        [formData, finalReview, pfJoinDate, remarks],
    );

    const handleSave = useCallback(
        async (shouldThrow = false) => {
            setSaving(true);
            setStatusMessage(null);
            try {
                if (!validateComplianceFields()) {
                    if (shouldThrow) throw new Error('Please resolve the validation errors.');
                    return false;
                }

                const payload = buildPayload();
                const employeeTargetId = targetEmployeeId || request?.employeeId;
                if (isEmployeeMasterView && !employeeTargetId) {
                    throw new Error('Employee reference not found for editing.');
                }

                const endpoint = isEmployeeMasterView
                    ? `/api/admin/employees/${employeeTargetId}/approval-view`
                    : `/api/admin/employee-approvals/${id}`;

                const response = await apiRequest(
                    endpoint,
                    {
                        method: 'PUT',
                        body: payload,
                    },
                    navigate,
                );

                setRequest(response.data);
                mapRequestToState(response.data);
                setStatusMessage({ type: 'success', message: 'Changes saved successfully.' });
                if (isEmployeeMasterView) {
                    setApprovedEmployeeSummary(
                        response.data?.employee ||
                        response.data?.employeeDetails ||
                        response.data?.employeeRecord ||
                        employeeRecord ||
                        null,
                    );
                    setShowSuccessModal(true);
                }
                return true;
            } catch (saveError) {
                console.error('Failed to save approval request', saveError);
                const missingFields = extractMissingFields(saveError?.message);
                highlightMissingFields(missingFields);
                setStatusMessage({
                    type: 'danger',
                    message: saveError.message || 'Failed to save approval details.',
                });
                if (shouldThrow) throw saveError;
                return false;
            } finally {
                setSaving(false);
            }
        },
        [
            buildPayload,
            highlightMissingFields,
            id,
            isEmployeeMasterView,
            mapRequestToState,
            navigate,
            employeeRecord,
            request?.employeeId,
            targetEmployeeId,
            validateComplianceFields,
        ],
    );

    const handleApprove = useCallback(async () => {
        if (!isRequestPending || isEmployeeMasterView) return;

        try {
            setActionLoading(true);
            setStatusMessage(null);

            await handleSave(true);

            const response = await apiRequest(
                `/api/admin/employee-approvals/${id}/approve`,
                { method: 'POST', body: { remarks } },
                navigate,
            );

            const updatedRequest = response.data?.request ?? null;
            if (updatedRequest) {
                setRequest(updatedRequest);
                mapRequestToState(updatedRequest);
            }

            const employeeSummary = response.data?.employee ?? null;
            if (employeeSummary) {
                setApprovedEmployeeSummary(employeeSummary);
                setShowSuccessModal(true);
                if (employeeSummary.showId) {
                    setFormData((prev) => ({
                        ...prev,
                        personal: { ...prev.personal, showId: employeeSummary.showId },
                    }));
                }
            } else {
                setApprovedEmployeeSummary(null);
                setShowSuccessModal(false);
            }

            setStatusMessage({
                type: 'success',
                message: response.message || 'Employee approval completed successfully.',
            });
        } catch (approveError) {
            console.error('Failed to approve request', approveError);
            const missingFields = extractMissingFields(approveError?.message);
            highlightMissingFields(missingFields);
            setStatusMessage({
                type: 'danger',
                message: approveError.message || 'Failed to approve the employee request.',
            });
        } finally {
            setActionLoading(false);
        }
    }, [handleSave, highlightMissingFields, id, isEmployeeMasterView, isRequestPending, mapRequestToState, navigate, remarks]);

    const handleReject = useCallback(async () => {
        if (!isRequestPending || isEmployeeMasterView) return;

        try {
            setActionLoading(true);
            setStatusMessage(null);

            const response = await apiRequest(
                `/api/admin/employee-approvals/${id}/reject`,
                { method: 'POST', body: { remarks: remarks.trim() || 'Request rejected' } },
                navigate,
            );

            navigate('/dashboard/pending-approvals', {
                replace: true,
                state: {
                    status: { type: 'warning', message: response.message || 'Employee request rejected.' },
                },
            });
        } catch (rejectError) {
            console.error('Failed to reject request', rejectError);
            setStatusMessage({
                type: 'danger',
                message: rejectError.message || 'Failed to reject the employee request.',
            });
        } finally {
            setActionLoading(false);
        }
    }, [id, isEmployeeMasterView, isRequestPending, navigate, remarks]);

    const handleSuccessModalClose = useCallback(() => {
        setShowSuccessModal(false);
    }, []);

    const handleSuccessModalBackToPending = useCallback(() => {
        setShowSuccessModal(false);
        navigate('/dashboard/pending-approvals', {
            replace: true,
            state: { status: { type: 'success', message: 'Employee approval completed successfully.' } },
        });
    }, [navigate]);

    const handleSuccessModalBackToEmployees = useCallback(() => {
        setShowSuccessModal(false);
        navigate('/employees', {
            replace: true,
            state: { status: { type: 'success', message: 'Employee details updated successfully.' } },
        });
    }, [navigate]);

    const handleSuccessModalDashboard = useCallback(() => {
        setShowSuccessModal(false);
        navigate('/dashboard', { replace: true });
    }, [navigate]);


    const normalizeWhatsAppNumber = useCallback((value) => {
        if (!value) return '';
        const digits = String(value).replace(/\D/g, '');
        if (!digits) return '';
        if (digits.length === 10) return `91${digits}`;
        return digits;
    }, []);

    const resolveOutletWhatsAppNumber = useCallback(() => {
        const candidates = [
            approvedEmployeeSummary?.outlet?.whatsappNumber,
            approvedEmployeeSummary?.outlet?.mobile,
            approvedEmployeeSummary?.outlet?.phone,
            employeeRecord?.outlet?.whatsappNumber,
            employeeRecord?.outlet?.mobile,
            employeeRecord?.outlet?.phone,
            request?.employee?.outlet?.whatsappNumber,
            request?.employee?.outlet?.mobile,
            request?.employee?.outlet?.phone,
        ];

        return candidates.find((candidate) => String(candidate ?? '').trim()) || '';
    }, [
        approvedEmployeeSummary?.outlet?.mobile,
        approvedEmployeeSummary?.outlet?.phone,
        approvedEmployeeSummary?.outlet?.whatsappNumber,
        employeeRecord?.outlet?.mobile,
        employeeRecord?.outlet?.phone,
        employeeRecord?.outlet?.whatsappNumber,
        request?.employee?.outlet?.mobile,
        request?.employee?.outlet?.phone,
        request?.employee?.outlet?.whatsappNumber,
    ]);

    const captureSuccessCardBlob = useCallback(async () => {
        if (!approvalSuccessCardRef.current) {
            throw new Error('Success card not available');
        }

        const canvas = await html2canvas(approvalSuccessCardRef.current, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: false,
            logging: false,
            scrollX: 0,
            scrollY: -window.scrollY,
            windowWidth: document.documentElement.offsetWidth,
            windowHeight: document.documentElement.offsetHeight,
        });

        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
                else reject(new Error('Unable to create approval image'));
            }, 'image/png');
        });
    }, []);

    const downloadApprovalImage = useCallback(
        (blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `employee-approval-${approvedEmployeeSummary?.employeeCode || approvedEmployeeSummary?.id || Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        },
        [approvedEmployeeSummary?.employeeCode, approvedEmployeeSummary?.id],
    );

    const handleCopyApprovalImage = useCallback(async () => {
        try {
            setIsCopyingApprovalImage(true);
            const blob = await captureSuccessCardBlob();

            if (!navigator.clipboard || !window.ClipboardItem) {
                downloadApprovalImage(blob);
                setStatusMessage({ type: 'success', message: 'Image download started. You can share it manually.' });
                return;
            }

            await navigator.clipboard.write([
                new window.ClipboardItem({
                    'image/png': blob,
                }),
            ]);

            setStatusMessage({ type: 'success', message: 'Approval image copied. Paste it in WhatsApp.' });
        } catch (copyError) {
            console.error('Failed to copy approval image', copyError);
            setStatusMessage({ type: 'danger', message: 'Unable to copy image. Please try again.' });
        } finally {
            setIsCopyingApprovalImage(false);
        }
    }, [captureSuccessCardBlob, downloadApprovalImage]);

    const handleShareApprovalToOutlet = useCallback(async () => {
        try {
            setIsSharingApprovalImage(true);
            const blob = await captureSuccessCardBlob();
            const file = new File([blob], 'employee-approval.png', { type: 'image/png' });
            const outletNumber = normalizeWhatsAppNumber(resolveOutletWhatsAppNumber());
            const text = `Employee approval completed: ${approvedEmployeeSummary?.name || employeeRecord?.name || ''}`;

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    title: 'Employee Approval',
                    text,
                    files: [file],
                });
                return;
            }

            if (navigator.clipboard && window.ClipboardItem) {
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'image/png': blob,
                    }),
                ]);
            } else {
                downloadApprovalImage(blob);
            }

            const whatsappUrl = outletNumber
                ? `https://wa.me/${outletNumber}?text=${encodeURIComponent(text)}`
                : `https://wa.me/?text=${encodeURIComponent(text)}`;

            window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
            setStatusMessage({ type: 'success', message: 'Approval image copied/downloaded. Paste it in WhatsApp chat.' });
        } catch (shareError) {
            console.error('Failed to share approval image', shareError);
            setStatusMessage({ type: 'danger', message: 'Unable to share approval image. Please try again.' });
        } finally {
            setIsSharingApprovalImage(false);
        }
    }, [
        approvedEmployeeSummary?.name,
        captureSuccessCardBlob,
        downloadApprovalImage,
        employeeRecord?.name,
        normalizeWhatsAppNumber,
        resolveOutletWhatsAppNumber,
    ]);

    const successTitle = useMemo(
        () => (isEmployeeMasterView ? 'Employee details updated successfully' : 'Employee Approval Completed!'),
        [isEmployeeMasterView],
    );

    const successSubtitle = useMemo(
        () =>
            isEmployeeMasterView
                ? 'The employee record has been updated with the following identifiers.'
                : 'The employee has been activated with the following identifiers.',
        [isEmployeeMasterView],
    );

    const successPrimaryAction = useMemo(
        () =>
            isEmployeeMasterView
                ? { label: 'Back to Employee List', onClick: handleSuccessModalBackToEmployees }
                : { label: 'Back to Pending List', onClick: handleSuccessModalBackToPending },
        [handleSuccessModalBackToEmployees, handleSuccessModalBackToPending, isEmployeeMasterView],
    );

    const resolvedOutletInfo = useMemo(() => {
        if (approvedEmployeeSummary?.outlet) return approvedEmployeeSummary.outlet;

        const outletIdRaw = formData.personal.outlet;
        const outletOption = outletOptions.find((option) => option.value === String(outletIdRaw));
        const outletName = outletOption?.label ?? finalReview.outletName ?? '';

        if (!outletIdRaw && !outletName) return null;

        const numericOutletId =
            typeof outletIdRaw === 'number'
                ? outletIdRaw
                : outletIdRaw && `${outletIdRaw}`.trim() !== ''
                    ? Number(outletIdRaw)
                    : null;

        return { id: Number.isNaN(numericOutletId) ? null : numericOutletId, name: outletName };
    }, [approvedEmployeeSummary?.outlet, finalReview.outletName, formData.personal.outlet, outletOptions]);

    const pfHistory = useMemo(() => {
        if (!employeeRecord?.pfHistory || employeeRecord.pfHistory.length === 0) return [];
        return employeeRecord.pfHistory.map((entry) => ({
            id: entry.id ?? `${entry.effectiveDate ?? 'unknown'}-${entry.uanNumber ?? 'uan'}`,
            effectiveDate: entry.effectiveDate ? toDisplayDate(entry.effectiveDate) : '—',
            uanNumber: entry.uanNumber || '—',
            esiNumber: entry.esiNumber || '—',
            insurerName: entry.insurerName || '—',
        }));
    }, [employeeRecord?.pfHistory]);

    const serviceTimeline = useMemo(() => {
        const defaultStartDate =
            finalReview.dateOfJoining ||
            formData.professional.fromDate ||
            employeeRecord?.professional?.fromDate ||
            employeeRecord?.createdAt ||
            null;

        return buildServiceHistoryTimeline(employeeRecord?.serviceHistory ?? [], {
            defaultStartDate,
            defaultOutlet: resolvedOutletInfo ?? employeeRecord?.outlet ?? null,
        });
    }, [
        employeeRecord?.createdAt,
        employeeRecord?.outlet,
        employeeRecord?.professional?.fromDate,
        employeeRecord?.serviceHistory,
        finalReview.dateOfJoining,
        formData.professional.fromDate,
        resolvedOutletInfo,
    ]);

    const selectedReferredEmployee = useMemo(() => {
        const key = finalReview.referredEmployeeId ? String(finalReview.referredEmployeeId) : '';
        return key ? employeeDetailsMap[key] ?? null : null;
    }, [employeeDetailsMap, finalReview.referredEmployeeId]);

    const selectedReferredEmployeeSalaryDisplay = selectedReferredEmployee ? formatCurrencyValue(selectedReferredEmployee.salary) : '—';

    const pfUanError = fieldErrors['pf.uanNumber'];
    const pfEsiError = fieldErrors['pf.esiNumber'];
    const bankAccountError = fieldErrors['bank.accountNumber'];
    const bankReAccountError = fieldErrors['bank.reAccountNumber'];
    const bankNameError = fieldErrors['bank.bankName'];
    const bankIfscError = fieldErrors['bank.ifscCode'];

    const statusBadgeClass = useMemo(() => {
        const status = (finalReview.employeeStatus || '').toLowerCase();
        if (status === 'active') return 'badge-status active';
        if (status === 'inactive') return 'badge-status inactive';
        return 'badge-status pending';
    }, [finalReview.employeeStatus]);

    const approvedEmployeePhotoUrl = useMemo(() => {
        const photo =
            approvedEmployeeSummary?.photo ||
            employeeRecord?.photo ||
            request?.employee?.photo ||
            request?.photo ||
            null;

        if (photo) {
            if (typeof photo === 'string') {
                return resolveFileFromCandidates(photo);
            }

            return resolveFileFromCandidates(
                photo.fileUrl,
                photo.url,
                photo.filePath,
                photo.path,
                photo.key,
            );
        }

        const photoDocument = formData.documents.find((document) => {
            const documentLabel = [
                document?.documentTypeName,
                document?.documentType,
                document?.type,
                document?.name,
                document?.fileName,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return documentLabel.includes('photo') || documentLabel.includes('profile') || documentLabel.includes('passport');
        });

        if (!photoDocument) return null;

        return resolveFileFromCandidates(
            photoDocument.fileUrl,
            photoDocument.url,
            photoDocument.filePath,
            photoDocument.path,
            photoDocument.key,
        );
    }, [
        approvedEmployeeSummary?.photo,
        employeeRecord?.photo,
        formData.documents,
        request?.employee?.photo,
        request?.photo,
    ]);

    const approvedEmployeeInitial = useMemo(() => {
        const sourceName = approvedEmployeeSummary?.name || formData.personal.name || '';
        if (!sourceName) return '';
        return sourceName.trim().charAt(0).toUpperCase();
    }, [approvedEmployeeSummary?.name, formData.personal.name]);

    const resolvedEmployeeIds = useMemo(() => {
        const normalize = (value) => {
            if (value === undefined || value === null) return '';
            const trimmed = `${value}`.trim();
            return trimmed;
        };

        const permId = [approvedEmployeeSummary?.permId, finalReview.permId, formData.personal.permId]
            .map(normalize)
            .find((candidate) => candidate);

        const tempId = [approvedEmployeeSummary?.tempId, finalReview.tempId, formData.personal.tempId]
            .map(normalize)
            .find((candidate) => candidate);

        const displayId = permId || tempId || '';
        const displayLabel = permId ? 'Permanent ID' : tempId ? 'Temporary ID' : 'Employee ID';

        return { permId: permId || '', tempId: tempId || '', displayId, displayLabel };
    }, [
        approvedEmployeeSummary?.permId,
        approvedEmployeeSummary?.tempId,
        finalReview.permId,
        finalReview.tempId,
        formData.personal.permId,
        formData.personal.tempId,
    ]);

    const successIdentifiers = useMemo(
        () => ({
            tempId: approvedEmployeeSummary?.tempId ?? resolvedEmployeeIds.tempId,
            permId: approvedEmployeeSummary?.permId ?? resolvedEmployeeIds.permId,
            displayId: resolvedEmployeeIds.displayId,
            displayLabel: resolvedEmployeeIds.displayLabel,
        }),
        [
            approvedEmployeeSummary?.permId,
            approvedEmployeeSummary?.tempId,
            resolvedEmployeeIds.permId,
            resolvedEmployeeIds.tempId,
            resolvedEmployeeIds.displayId,
            resolvedEmployeeIds.displayLabel,
        ],
    );

    const successOutletName = useMemo(() => {
        if (approvedEmployeeSummary?.outlet?.name) return approvedEmployeeSummary.outlet.name;
        if (finalReview.outletName) return finalReview.outletName;

        const outletValue = finalReview.outletId || formData.personal.outlet;
        if (outletValue && outletOptions.length) {
            const match = outletOptions.find((option) => option.value === String(outletValue));
            if (match) return match.label;
        }

        return '';
    }, [approvedEmployeeSummary?.outlet?.name, finalReview.outletId, finalReview.outletName, formData.personal.outlet, outletOptions]);


    const successDesignationLabel = useMemo(() => {
        const summaryDesignation =
            approvedEmployeeSummary?.designationName ||
            approvedEmployeeSummary?.designation?.name ||
            approvedEmployeeSummary?.designation?.designation ||
            approvedEmployeeSummary?.designation ||
            approvedEmployeeSummary?.professional?.designation ||
            employeeRecord?.designationName ||
            employeeRecord?.designation?.name ||
            employeeRecord?.designation?.designation ||
            employeeRecord?.designation ||
            employeeRecord?.professional?.designation ||
            finalReviewDesignationLabel ||
            formData.professional.designation ||
            '';

        if (!summaryDesignation) return '';
        const value = String(summaryDesignation).trim();
        const matchedById = designationOptions.find((option) => option.value === value);
        if (matchedById) return matchedById.label;
        const matchedByName = designationOptions.find((option) => option.label?.toLowerCase() === value.toLowerCase());
        return matchedByName?.label || value;
    }, [
        approvedEmployeeSummary?.designation,
        approvedEmployeeSummary?.designationName,
        approvedEmployeeSummary?.professional?.designation,
        designationOptions,
        employeeRecord?.designation,
        employeeRecord?.designationName,
        employeeRecord?.professional?.designation,
        finalReviewDesignationLabel,
        formData.professional.designation,
    ]);

    useEffect(() => {
        const outletValue = formData.personal.outlet ? String(formData.personal.outlet) : '';
        const selectedOutlet = outletOptions.find((option) => option.value === outletValue);

        setFinalReview((prev) => {
            const updates = {};

            if (prev.outletId !== outletValue) updates.outletId = outletValue;
            if (selectedOutlet && prev.outletName !== selectedOutlet.label) updates.outletName = selectedOutlet.label;

            return Object.keys(updates).length ? { ...prev, ...updates } : prev;
        });
    }, [formData.personal.outlet, outletOptions]);

    // --------------------------------------------------------------------------------
    // RENDER (same as your code) - ONLY changed 2 UI handlers (Experience From + DOJ mapping)
    // --------------------------------------------------------------------------------

    return (
        <DashboardLayout title="Employee Approval Review">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                    &larr; Back to Pending List
                </button>
                <span className={statusBadgeClass}>{finalReview.employeeStatus}</span>
            </div>

            {statusMessage && <div className={`alert alert-${statusMessage.type}`} role="alert">{statusMessage.message}</div>}
            {error && <div className="alert alert-danger" role="alert">{error}</div>}

            <div className="approval-view-container">
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-danger" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="row g-4">
                        {/* ---- LEFT (your full UI) ---- */}
                        <div className="col-lg-8">
                            <div className="approval-section">
                                <div className="card approval-form-card">
                                    <div className="card-body">
                                        <SectionCardHeader
                                            icon={FiUser}
                                            title="Personal Information"
                                            subtitle="Review and update the employee's core details."
                                        />
                                        {resolvedEmployeeIds.displayId ? (
                                            <div className="approval-id-badge mb-3">
                                                <span className="badge rounded-pill bg-danger-subtle text-danger fw-semibold">
                                                    {resolvedEmployeeIds.displayLabel}: {resolvedEmployeeIds.displayId}
                                                </span>
                                            </div>
                                        ) : null}
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Employee Name</label>
                                                <input
                                                    type="text"
                                                    className={`form-control${hasFieldError('personal', 'name') ? ' is-invalid' : ''}`}
                                                    ref={(node) => {
                                                        if (node) {
                                                            fieldRefs.current['personal.name'] = node;
                                                        } else {
                                                            delete fieldRefs.current['personal.name'];
                                                        }
                                                    }}
                                                    value={formData.personal.name}
                                                    onChange={(event) => updateSectionField('personal', 'name', event.target.value)}
                                                />
                                                {hasFieldError('personal', 'name') && (
                                                    <div className="invalid-feedback">{REQUIRED_FIELD_MESSAGES.name}</div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Father's Name</label>
                                                <input
                                                    type="text"
                                                    className={`form-control${hasFieldError('personal', 'fatherName') ? ' is-invalid' : ''}`}
                                                    ref={(node) => {
                                                        if (node) {
                                                            fieldRefs.current['personal.fatherName'] = node;
                                                        } else {
                                                            delete fieldRefs.current['personal.fatherName'];
                                                        }
                                                    }}
                                                    value={formData.personal.fatherName}
                                                    onChange={(event) => updateSectionField('personal', 'fatherName', event.target.value)}
                                                />
                                                {hasFieldError('personal', 'fatherName') && (
                                                    <div className="invalid-feedback">{REQUIRED_FIELD_MESSAGES.fatherName}</div>
                                                )}
                                            </div>
                                            {showSpouseNameField && (
                                                <div className="col-md-6">
                                                    <label className="form-label">Spouse Name</label>
                                                    <input
                                                        type="text"
                                                        className={`form-control${hasFieldError('personal', 'spouseName') ? ' is-invalid' : ''}`}
                                                        ref={(node) => {
                                                            if (node) fieldRefs.current['personal.spouseName'] = node;
                                                            else delete fieldRefs.current['personal.spouseName'];
                                                        }}
                                                        value={formData.personal.spouseName}
                                                        onChange={(event) => updateSectionField('personal', 'spouseName', event.target.value)}
                                                    />
                                                    {hasFieldError('personal', 'spouseName') && (
                                                        <div className="invalid-feedback">{REQUIRED_FIELD_MESSAGES.spouseName}</div>
                                                    )}
                                                </div>
                                            )}
                                            <div className="col-md-6">
                                                <label className="form-label">Date of Birth</label>
                                                <input
                                                    type="date"
                                                    className={`form-control${hasFieldError('personal', 'dob') ? ' is-invalid' : ''}`}
                                                    ref={(node) => {
                                                        if (node) {
                                                            fieldRefs.current['personal.dob'] = node;
                                                        } else {
                                                            delete fieldRefs.current['personal.dob'];
                                                        }
                                                    }}
                                                    value={formData.personal.dob}
                                                    onChange={(event) => updateSectionField('personal', 'dob', event.target.value)}
                                                />
                                                {hasFieldError('personal', 'dob') && (
                                                    <div className="invalid-feedback">{REQUIRED_FIELD_MESSAGES.dob}</div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Gender</label>
                                                <select
                                                    className={`form-select${hasFieldError('personal', 'gender') ? ' is-invalid' : ''}`}
                                                    ref={(node) => {
                                                        if (node) {
                                                            fieldRefs.current['personal.gender'] = node;
                                                        } else {
                                                            delete fieldRefs.current['personal.gender'];
                                                        }
                                                    }}
                                                    value={formData.personal.gender}
                                                    onChange={(event) => updateSectionField('personal', 'gender', event.target.value)}
                                                >
                                                    <option value="">Select Gender</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                    <option value="other">Other</option>
                                                </select>
                                                {hasFieldError('personal', 'gender') && (
                                                    <div className="invalid-feedback">{REQUIRED_FIELD_MESSAGES.gender}</div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Marital Status</label>
                                                <select
                                                    className={`form-select${hasFieldError('personal', 'maritalStatus') ? ' is-invalid' : ''}`}
                                                    ref={(node) => {
                                                        if (node) {
                                                            fieldRefs.current['personal.maritalStatus'] = node;
                                                        } else {
                                                            delete fieldRefs.current['personal.maritalStatus'];
                                                        }
                                                    }}
                                                    value={formData.personal.maritalStatus}
                                                    onChange={(event) => updateSectionField('personal', 'maritalStatus', event.target.value)}
                                                >
                                                    <option value="">Select Marital Status</option>
                                                    <option value="single">Single</option>
                                                    <option value="married">Married</option>
                                                    <option value="divorced">Divorced</option>
                                                    <option value="widowed">Widowed</option>
                                                </select>
                                                {hasFieldError('personal', 'maritalStatus') && (
                                                    <div className="invalid-feedback">{REQUIRED_FIELD_MESSAGES.maritalStatus}</div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Mobile Number</label>
                                                <input
                                                    type="text"
                                                    className={`form-control${hasFieldError('personal', 'phone') ? ' is-invalid' : ''}`}
                                                    ref={(node) => {
                                                        if (node) {
                                                            fieldRefs.current['personal.phone'] = node;
                                                        } else {
                                                            delete fieldRefs.current['personal.phone'];
                                                        }
                                                    }}
                                                    value={formData.personal.phone}
                                                    onChange={(event) => updateSectionField('personal', 'phone', event.target.value)}
                                                />
                                                {hasFieldError('personal', 'phone') && (
                                                    <div className="invalid-feedback">{REQUIRED_FIELD_MESSAGES.phone}</div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Email</label>
                                                <input
                                                    type="email"
                                                    className={`form-control${hasFieldError('personal', 'email') ? ' is-invalid' : ''}`}
                                                    ref={(node) => {
                                                        if (node) {
                                                            fieldRefs.current['personal.email'] = node;
                                                        } else {
                                                            delete fieldRefs.current['personal.email'];
                                                        }
                                                    }}
                                                    value={formData.personal.email}
                                                    onChange={(event) => updateSectionField('personal', 'email', event.target.value)}
                                                />
                                                {hasFieldError('personal', 'email') && (
                                                    <div className="invalid-feedback">{REQUIRED_FIELD_MESSAGES.email}</div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Aadhaar Number</label>
                                                <input
                                                    type="text"
                                                    className={`form-control${hasFieldError('personal', 'aadhar') ? ' is-invalid' : ''}`}
                                                    ref={(node) => {
                                                        if (node) {
                                                            fieldRefs.current['personal.aadhar'] = node;
                                                        } else {
                                                            delete fieldRefs.current['personal.aadhar'];
                                                        }
                                                    }}
                                                    value={formData.personal.aadhar}
                                                    onChange={(event) => updateSectionField('personal', 'aadhar', event.target.value)}
                                                />
                                                {hasFieldError('personal', 'aadhar') && (
                                                    <div className="invalid-feedback">{REQUIRED_FIELD_MESSAGES.aadhar}</div>
                                                )}
                                            </div>
                                            {formData.personal.status === 'inactive' && (
                                                <>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Relieving Reason</label>
                                                        <select
                                                            className="form-select"
                                                            value={formData.personal.inactiveReason || 'relieving'}
                                                            onChange={(event) => {
                                                                updateSectionField('personal', 'inactiveReason', event.target.value);
                                                                updateFinalReview('inactiveReason', event.target.value);
                                                            }}
                                                        >
                                                            <option value="relieving">Relieving</option>
                                                            <option value="other">Other</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Relieving Date</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            value={formData.personal.relievingDate}
                                                            onChange={(event) => {
                                                                updateSectionField('personal', 'relievingDate', event.target.value);
                                                                updateFinalReview('relievingDate', event.target.value);
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label">Relieving Notes</label>
                                                        <textarea
                                                            className="form-control"
                                                            rows={2}
                                                            value={formData.personal.inactiveNotes}
                                                            onChange={(event) => {
                                                                updateSectionField('personal', 'inactiveNotes', event.target.value);
                                                                updateFinalReview('inactiveNotes', event.target.value);
                                                            }}
                                                            placeholder="Add notes about the relieving reason"
                                                        />
                                                    </div>
                                                </>
                                            )}
                                            <div className="col-12">
                                                <label className="form-label">Current Address</label>
                                                <textarea
                                                    className={`form-control${hasFieldError('personal', 'currentAddress') ? ' is-invalid' : ''}`}
                                                    ref={(node) => {
                                                        if (node) {
                                                            fieldRefs.current['personal.currentAddress'] = node;
                                                        } else {
                                                            delete fieldRefs.current['personal.currentAddress'];
                                                        }
                                                    }}
                                                    rows={2}
                                                    value={formData.personal.currentAddress}
                                                    onChange={(event) => updateSectionField('personal', 'currentAddress', event.target.value)}
                                                />
                                                {hasFieldError('personal', 'currentAddress') && (
                                                    <div className="invalid-feedback">{REQUIRED_FIELD_MESSAGES.currentAddress}</div>
                                                )}
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label">Permanent Address</label>
                                                <textarea
                                                    className={`form-control${hasFieldError('personal', 'permanentAddress') ? ' is-invalid' : ''}`}
                                                    ref={(node) => {
                                                        if (node) {
                                                            fieldRefs.current['personal.permanentAddress'] = node;
                                                        } else {
                                                            delete fieldRefs.current['personal.permanentAddress'];
                                                        }
                                                    }}
                                                    rows={2}
                                                    value={formData.personal.permanentAddress}
                                                    onChange={(event) => updateSectionField('personal', 'permanentAddress', event.target.value)}
                                                />
                                                {hasFieldError('personal', 'permanentAddress') && (
                                                    <div className="invalid-feedback">{REQUIRED_FIELD_MESSAGES.permanentAddress}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="approval-section">
                                <div className="card approval-form-card">
                                    <div className="card-body">
                                        <SectionCardHeader
                                            icon={FiShield}
                                            title="PF & Insurance Information"
                                            subtitle="Maintain statutory identifiers and insurer details."
                                        />
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <label className="form-label">UAN Number</label>
                                                <input
                                                    type="text"
                                                    className={`form-control${pfUanError ? ' is-invalid' : ''}`}
                                                    value={formData.pf.uanNumber}
                                                    onChange={(event) => updateSectionField('pf', 'uanNumber', event.target.value)}
                                                />
                                                {pfUanError && <div className="invalid-feedback">{pfUanError}</div>}
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">ESI Number</label>
                                                <input
                                                    type="text"
                                                    className={`form-control${pfEsiError ? ' is-invalid' : ''}`}
                                                    value={formData.pf.esiNumber}
                                                    onChange={(event) => updateSectionField('pf', 'esiNumber', event.target.value)}
                                                />
                                                {pfEsiError && <div className="invalid-feedback">{pfEsiError}</div>}
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Insurance Number</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={finalReview.insuranceNumber}
                                                    onChange={(event) => updateFinalReview('insuranceNumber', event.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Insurer Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={finalReview.insurerName}
                                                    onChange={(event) => updateFinalReview('insurerName', event.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">PF Join Date</label>
                                                <div className="input-group">
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={pfJoinDate}
                                                        onChange={(event) => setPfJoinDate(event.target.value)}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        disabled={isPfJoinLoading || !request?.employeeId}
                                                        onClick={fetchPfJoinDate}
                                                    >
                                                        {isPfJoinLoading ? 'Fetching...' : 'Get PF Join Date'}
                                                    </button>
                                                </div>
                                                {pfJoinError ? (
                                                    <div className="text-danger small mt-1">{pfJoinError}</div>
                                                ) : !request?.employeeId ? (
                                                    <div className="text-muted small mt-1">
                                                        Available after the employee profile exists.
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="approval-section">
                                <div className="card">
                                    <div className="card-body">
                                        <h6 className="approval-section-title mb-3">PF History</h6>
                                        {pfHistory.length === 0 ? (
                                            <p className="text-muted mb-0">No PF history recorded.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-sm align-middle">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">Effective Date</th>
                                                            <th scope="col">UAN Number</th>
                                                            <th scope="col">ESI Number</th>
                                                            <th scope="col">Insurer</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {pfHistory.map((entry) => (
                                                            <tr key={entry.id}>
                                                                <td>{entry.effectiveDate}</td>
                                                                <td>{entry.uanNumber}</td>
                                                                <td>{entry.esiNumber}</td>
                                                                <td>{entry.insurerName}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="approval-section">
                                <div className="card approval-form-card">
                                    <div className="card-body">
                                        <SectionCardHeader
                                            icon={FiCreditCard}
                                            title="Banking Information"
                                            subtitle="Confirm salary disbursement account details."
                                        />
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Account Number</label>
                                                <input
                                                    type="text"
                                                    className={`form-control${bankAccountError ? ' is-invalid' : ''}`}
                                                    value={formData.bank.accountNumber}
                                                    onChange={(event) => updateSectionField('bank', 'accountNumber', event.target.value)}
                                                />
                                                {bankAccountError && <div className="invalid-feedback">{bankAccountError}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Re-enter Account Number</label>
                                                <input
                                                    type="text"
                                                    className={`form-control${bankReAccountError ? ' is-invalid' : ''}`}
                                                    value={formData.bank.reAccountNumber}
                                                    onChange={(event) => updateSectionField('bank', 'reAccountNumber', event.target.value)}
                                                />
                                                {bankReAccountError && <div className="invalid-feedback">{bankReAccountError}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Bank Name</label>
                                                <input
                                                    type="text"
                                                    className={`form-control${bankNameError ? ' is-invalid' : ''}`}
                                                    value={formData.bank.bankName}
                                                    onChange={(event) => updateSectionField('bank', 'bankName', event.target.value)}
                                                />
                                                {bankNameError && <div className="invalid-feedback">{bankNameError}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">IFSC Code</label>
                                                <input
                                                    type="text"
                                                    className={`form-control${bankIfscError ? ' is-invalid' : ''}`}
                                                    value={formData.bank.ifscCode}
                                                    onChange={(event) => updateSectionField('bank', 'ifscCode', event.target.value)}
                                                />
                                                {bankIfscError && <div className="invalid-feedback">{bankIfscError}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">PAN Number</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.bank.panCard}
                                                    onChange={(event) => updateSectionField('bank', 'panCard', event.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Driving License</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.bank.drivingLicense}
                                                    onChange={(event) => updateSectionField('bank', 'drivingLicense', event.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="approval-section">
                                <div className="card approval-form-card">
                                    <div className="card-body">
                                        <SectionCardHeader
                                            icon={FiBookOpen}
                                            title="Education Information"
                                            subtitle="Review submitted qualifications and supporting documents."
                                        />
                                        {hasQualificationEntries ? (
                                            <div className="d-flex flex-column gap-3">
                                                {qualificationEntries.map((entry) => {
                                                    const documentField = entry.fields.find((field) =>
                                                        Object.prototype.hasOwnProperty.call(field, 'documentUrl'),
                                                    );
                                                    const infoFields = entry.fields.filter((field) => field !== documentField);

                                                    return (
                                                        <div key={entry.id} className="border rounded-3 p-3">
                                                            <div className="d-flex flex-column gap-3">
                                                                <div>
                                                                    <h6 className="mb-2 text-uppercase">{entry.title}</h6>
                                                                    <div className="row g-3">
                                                                        {infoFields.map((field) => (
                                                                            <div key={field.label} className="col-md-6">
                                                                                <div className="text-muted small">{field.label}</div>
                                                                                <div className="fw-semibold text-dark">{field.value}</div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                                                    <span className="text-muted small">
                                                                        {documentField?.label || 'Supporting Document'}
                                                                    </span>
                                                                    {entry.documentDescriptor && documentField?.value !== '—' ? (
                                                                        <div className="d-flex flex-wrap gap-2">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                                                                                onClick={() => handleViewDocument(entry.documentDescriptor)}
                                                                            >
                                                                                <Eye size={16} />
                                                                                View
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                                                                onClick={() => handleDownloadDocument(entry.documentDescriptor)}
                                                                            >
                                                                                <FiDownload size={16} />
                                                                                Download
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <span className="badge bg-light text-dark">
                                                                            {documentField?.value || 'No document uploaded'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-muted mb-0">No educational qualifications provided.</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* PROFESSIONAL SECTION (ONLY this onChange fixed) */}
                            <div className="approval-section">
                                <div className="card approval-form-card">
                                    <div className="card-body">
                                        <SectionCardHeader
                                            icon={FiBriefcase}
                                            title="Professional Information"
                                            subtitle="Validate previous experience and designation details."
                                        />
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Last Company</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.professional.lastCompany}
                                                    onChange={(event) => updateSectionField('professional', 'lastCompany', event.target.value)}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">Previous Company Designation</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter previous company designation"
                                                    value={formData.professional.designation}
                                                    onChange={(event) => updateSectionField('professional', 'designation', event.target.value)}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">Experience From</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={formData.professional.fromDate}
                                                    onChange={(event) => {
                                                        // ✅ FIX: do NOT update finalReview.dateOfJoining here
                                                        updateSectionField('professional', 'fromDate', event.target.value);
                                                    }}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">Experience To</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={formData.professional.toDate}
                                                    onChange={(event) => updateSectionField('professional', 'toDate', event.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            <div className="approval-section">
                                <div className="card approval-form-card">
                                    <div className="card-body">
                                        <SectionCardHeader
                                            icon={FiUsers}
                                            title="Family Members"
                                            subtitle="Submitted details cannot be modified from here."
                                        />
                                        {formData.family.length === 0 ? (
                                            <p className="text-muted mb-0">No family members submitted.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">Name</th>
                                                            <th scope="col">Relation</th>
                                                            <th scope="col">Contact</th>
                                                            <th scope="col">Date of Birth</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formData.family.map((member, index) => (
                                                            <tr key={`${member.id || member.name}-${index}`}>
                                                                <td>{member.name || '—'}</td>
                                                                <td>{member.relation || '—'}</td>
                                                                <td>{member.mobile || '—'}</td>
                                                                <td>{member.dob ? toDisplayDate(member.dob) : '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="approval-section">
                                <div className="card approval-form-card">
                                    <div className="card-body">
                                        <SectionCardHeader
                                            icon={FiFileText}
                                            title="Documents"
                                            subtitle="Review uploaded employee documents."
                                        />
                                        {formData.documents.length === 0 ? (
                                            <p className="text-muted mb-0">No documents uploaded.</p>
                                        ) : (
                                            <ul className="list-group list-group-flush">
                                                {formData.documents.map((document, index) => {
                                                    const canView = canViewDocument(document);
                                                    const documentLabel = document.documentTypeName || document.documentType || 'Document';

                                                    return (
                                                        <li
                                                            key={document.id || `${document.documentType || 'document'}-${document.fileName || index}`}
                                                            className="list-group-item px-0"
                                                        >
                                                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                                                <div className="me-3">
                                                                    <div className="fw-semibold">{documentLabel}</div>
                                                                    <div className="text-muted small text-break">
                                                                        {document.fileName || 'No file name provided'}
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                                                                        onClick={() => handleViewDocument(document)}
                                                                        disabled={!canView}
                                                                    >
                                                                        <Eye size={16} />
                                                                        View
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                                                        onClick={() => handleDownloadDocument(document)}
                                                                        disabled={!canView}
                                                                    >
                                                                        <FiDownload size={16} />
                                                                        Download
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                                                                        onClick={() => handleRemoveDocument(index)}
                                                                        disabled={!isRequestPending}
                                                                    >
                                                                        <Trash size={16} />
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="approval-section">
                                <div className="card final-review-card">
                                    <div className="card-body">
                                        <SectionCardHeader
                                            icon={FiCheckSquare}
                                            title="Final Review Details"
                                            subtitle="Confirm the final allocation and compensation details."
                                        />
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Company</label>
                                                <select
                                                    className="form-select"
                                                    value={finalReview.companyId}
                                                    onChange={(event) => {
                                                        const value = event.target.value;
                                                        updateFinalReview('companyId', value);
                                                        const selectedOption = companyOptions.find(
                                                            (option) => option.value === value,
                                                        );
                                                        updateFinalReview(
                                                            'companyName',
                                                            selectedOption ? selectedOption.label : '',
                                                        );
                                                    }}
                                                    ref={(node) => {
                                                        if (node) {
                                                            fieldRefs.current['personal.companyId'] = node;
                                                        } else {
                                                            delete fieldRefs.current['personal.companyId'];
                                                        }
                                                    }}
                                                >
                                                    <option value="">Select Company</option>
                                                    {companyOptions.map((company) => (
                                                        <option key={company.value} value={company.value}>
                                                            {company.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Outlet</label>
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Type to search outlets"
                                                        value={outletSearchTerm}
                                                        onChange={(event) => {
                                                            const value = event.target.value;
                                                            setOutletSearchTerm(value);
                                                            if (finalReview.outletId || formData.personal.outlet) {
                                                                updateSectionField('personal', 'outlet', '');
                                                                updateFinalReview('outletId', '');
                                                                updateFinalReview('outletName', '');
                                                            }
                                                            if (value.trim().length >= 2) {
                                                                searchOutlets(value);
                                                            }
                                                        }}
                                                        onFocus={() => {
                                                            setIsOutletFocused(true);
                                                            if (outletSearchTerm.trim().length >= 2) {
                                                                searchOutlets(outletSearchTerm);
                                                            }
                                                        }}
                                                        onBlur={() => {
                                                            setIsOutletFocused(false);
                                                            clearOutletOptions();
                                                        }}
                                                    />
                                                    {isOutletSearching && (
                                                        <div className="position-absolute top-50 end-0 translate-middle-y pe-3 text-muted small">
                                                            Searching...
                                                        </div>
                                                    )}
                                                    {isOutletFocused && outletOptions.length > 0 && (
                                                        <div
                                                            className="dropdown-menu show w-100"
                                                            style={{ maxHeight: '220px', overflowY: 'auto' }}
                                                        >
                                                            {outletOptions.map((outlet) => (
                                                                <button
                                                                    key={outlet.value}
                                                                    type="button"
                                                                    className="dropdown-item text-wrap"
                                                                    onMouseDown={(event) => {
                                                                        event.preventDefault();
                                                                        updateSectionField('personal', 'outlet', outlet.value);
                                                                        updateFinalReview('outletId', outlet.value);
                                                                        updateFinalReview('outletName', outlet.label);
                                                                        setOutletSearchTerm(outlet.label);
                                                                        clearOutletOptions();
                                                                    }}
                                                                >
                                                                    {outlet.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="form-text mt-1">Type at least 2 characters to search outlets.</div>
                                                {outletSearchError && (
                                                    <div className="text-danger small mt-1">{outletSearchError}</div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Date of Joining</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={finalReview.dateOfJoining}
                                                    onChange={(event) => {
                                                        setIsDateOfJoiningManual(true);
                                                        updateFinalReview('dateOfJoining', event.target.value);
                                                    }}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Temporary ID</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={finalReview.tempId}
                                                    placeholder={
                                                        latestIds.nextTempId
                                                            ? `Suggested next ID: ${latestIds.nextTempId}`
                                                            : 'Enter temporary ID'
                                                    }
                                                    onChange={(event) => updateFinalReview('tempId', event.target.value)}
                                                />
                                                <div className="form-text">
                                                    {latestIds.latestTempId
                                                        ? `Last assigned temporary ID: ${latestIds.latestTempId}`
                                                        : 'Temporary IDs start from 30001'}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Permanent ID</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={finalReview.permId}
                                                    placeholder={
                                                        latestIds.nextPermanentId
                                                            ? `Suggested next ID: ${latestIds.nextPermanentId}`
                                                            : 'Enter permanent ID'
                                                    }
                                                    onChange={(event) => updateFinalReview('permId', event.target.value)}
                                                />
                                                <div className="form-text">
                                                    {latestIds.latestPermanentId
                                                        ? `Last assigned permanent ID: ${latestIds.latestPermanentId}`
                                                        : 'Permanent IDs start from 10001'}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Salary</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={finalReview.salary || employeeRecord?.salary || ''}
                                                    onChange={(event) => updateFinalReview('salary', event.target.value)}
                                                />
                                                {employeeRecord?.salary ? (
                                                    <div className="text-muted small mt-1">
                                                        Existing Salary: {formatCurrencyValue(employeeRecord?.salary)}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Designation</label>
                                                <select
                                                    className="form-select"
                                                    value={finalReview.designation}
                                                    onChange={(event) => {
                                                        setIsDesignationManual(true);
                                                        updateFinalReview('designation', event.target.value);
                                                    }}
                                                >
                                                    <option value="">Select Designation</option>
                                                    {resolvedFinalReviewDesignationOptions.map((designation) => (
                                                        <option key={designation.value} value={designation.value}>
                                                            {designation.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Referred Employee</label>
                                                <select
                                                    className="form-select"
                                                    value={finalReview.referredEmployeeId}
                                                    onChange={(event) => updateFinalReview('referredEmployeeId', event.target.value)}
                                                >
                                                    <option value="">Select Employee</option>
                                                    {employeeOptions.map((employee) => (
                                                        <option key={employee.value} value={employee.value}>
                                                            {employee.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-12">
                                                <div className="approval-subsection">
                                                    <div className="approval-subsection-header">
                                                        <FiClock size={18} />
                                                        <span>Working Time Allocation</span>
                                                    </div>
                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <label className="form-label">Employment Type</label>
                                                            <select
                                                                className="form-select"
                                                                value={formData.workingTime.type}
                                                                onChange={(event) => updateSectionField('workingTime', 'type', event.target.value)}
                                                            >
                                                                <option value="">Select Type</option>
                                                                <option value="fullTime">Full Time</option>
                                                                <option value="partTime">Part Time</option>
                                                                <option value="contract">Contract</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-12">
                                                            <WorkingShiftAllocation
                                                                shifts={formData.workingTime.shifts}
                                                                onChange={handleWorkingShiftsChange}
                                                                shiftOptions={shiftOptions}
                                                                loading={shiftOptionsLoading}
                                                                error={shiftOptionsError}
                                                                validationError={fieldErrors['workingTime.shifts']}
                                                            />
                                                            <p className="text-muted small mt-2">
                                                                Add one or more shifts for the employee. The primary shift will be used for payroll and
                                                                reporting.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="approval-section">
                                <div className="card service-history-card">
                                    <div className="card-body">
                                        <SectionCardHeader
                                            icon={FiLayers}
                                            title={`Service History${formData.personal.name ? ` for ${formData.personal.name}` : ''}`}
                                            subtitle="Automatically calculated from joining and relieving records."
                                        />
                                        {serviceTimeline.hasHistory ? (
                                            <>
                                                {serviceTimeline.totalDurationLabel ? (
                                                    <div className="service-history-summary">
                                                        <strong>Total Service Duration:</strong> {serviceTimeline.totalDurationLabel}
                                                    </div>
                                                ) : null}
                                                <ul className="list-unstyled mb-0 service-history-list">
                                                    {serviceTimeline.periods.map((period) => (
                                                        <li key={period.id} className="history-item">
                                                            <strong>{period.timelineLabel}</strong>
                                                            {period.reason && (
                                                                <div className="text-muted small">Reason: {period.reason}</div>
                                                            )}
                                                            {period.notes && (
                                                                <div className="text-muted small">{period.notes}</div>
                                                            )}
                                                            {period.outlet?.name && (
                                                                <div className="text-muted small">Outlet: {period.outlet.name}</div>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>
                                        ) : (
                                            <p className="text-muted mb-0">No previous service history available.</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="approval-section">
                                <div className="card">
                                    <div className="card-body">
                                        <SectionCardHeader
                                            icon={FiMessageSquare}
                                            title="Remarks"
                                            subtitle={
                                                isEmployeeMasterView
                                                    ? 'Add any notes you want to keep alongside these edits.'
                                                    : 'Add any notes for approval or rejection.'
                                            }
                                        />
                                        <textarea
                                            className="form-control"
                                            rows={4}
                                            value={remarks}
                                            onChange={(event) => setRemarks(event.target.value)}
                                            placeholder="Add notes for approval or rejection"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex gap-3 mt-4">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => handleSave()}
                                    disabled={saving || actionLoading || !isRequestPending}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                                {!isEmployeeMasterView && (
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger"
                                            onClick={handleReject}
                                            disabled={actionLoading || !isRequestPending}
                                        >
                                            {actionLoading ? 'Processing...' : 'Reject'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-success"
                                            onClick={handleApprove}
                                            disabled={actionLoading || !isRequestPending}
                                        >
                                            {actionLoading ? 'Processing...' : 'Approve'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* ---- RIGHT SUMMARY (your UI same) ---- */}
                        <div className="col-lg-4">
                            <div className="card summary-card">
                                <div className="card-body text-center">
                                    <img
                                        src={confirmIllustration}
                                        alt="Employee confirmation"
                                        className="summary-illustration mb-4"
                                    />
                                    <div className="summary-details">
                                        <h6 className="mb-3">Final Summary</h6>
                                        <div className="summary-item">
                                            <span>Employee</span>
                                            <strong>{formData.personal.name || '—'}</strong>
                                        </div>
                                        <div className="summary-item">
                                            <span>{resolvedEmployeeIds.displayLabel}</span>
                                            <strong>{resolvedEmployeeIds.displayId || '—'}</strong>
                                        </div>
                                        <div className="summary-item">
                                            <span>Outlet</span>
                                            <strong>{finalReview.outletName || '—'}</strong>
                                        </div>
                                        <div className="summary-item">
                                            <span>Date of Joining</span>
                                            <strong>{finalReview.dateOfJoining ? toDisplayDate(finalReview.dateOfJoining) : '—'}</strong>
                                        </div>
                                        <div className="summary-item">
                                            <span>Salary</span>
                                            <strong>{finalReview.salary || employeeRecord?.salary || '—'}</strong>
                                        </div>
                                        <div className="summary-item">
                                            <span>Designation</span>
                                            <strong>{finalReviewDesignationLabel || '—'}</strong>
                                        </div>
                                        <div className="summary-item">
                                            <span>Insurance Number</span>
                                            <strong>{finalReview.insuranceNumber || '—'}</strong>
                                        </div>
                                        <div className="summary-item">
                                            <span>Insurer Name</span>
                                            <strong>{finalReview.insurerName || formData.pf.insurerName || '—'}</strong>
                                        </div>
                                        <div className="summary-item">
                                            <span>Referred By</span>
                                            <strong>
                                                {finalReview.referredEmployeeId
                                                    ? employeeOptions.find((option) => option.value === finalReview.referredEmployeeId)?.label || '—'
                                                    : '—'}
                                            </strong>
                                        </div>
                                        <div className="summary-item">
                                            <span>Referrer Salary</span>
                                            <strong>{selectedReferredEmployeeSalaryDisplay}</strong>
                                        </div>
                                        {serviceTimeline.totalDurationLabel ? (
                                            <div className="summary-item">
                                                <span>Service Duration</span>
                                                <strong>{serviceTimeline.totalDurationLabel}</strong>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {documentPreview && (
                <div
                    className="document-preview-backdrop"
                    role="dialog"
                    aria-modal="true"
                    onClick={closeDocumentPreview}
                >
                    <div className="document-preview-modal" onClick={(event) => event.stopPropagation()}>
                        <button
                            type="button"
                            className="btn-close document-preview-close"
                            aria-label="Close"
                            onClick={closeDocumentPreview}
                        />
                        <div className="document-preview-body">
                            <div className="document-preview-title">{documentPreview.title || 'Document Preview'}</div>
                            <div className="document-preview-content">
                                <img src={documentPreview.url} alt={documentPreview.title || 'Document'} />
                            </div>
                            <div className="document-preview-actions">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                    onClick={() =>
                                        handleDownloadDocument({
                                            fileUrl: documentPreview.url,
                                            fileName: documentPreview.fileName,
                                            mimeType: documentPreview.mimeType,
                                        })
                                    }
                                >
                                    <FiDownload size={16} />
                                    Download
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showSuccessModal && (
                <div
                    className="approval-success-backdrop"
                    role="dialog"
                    aria-modal="true"
                    onClick={handleSuccessModalClose}
                >
                    <div
                        className="approval-success-modal"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="btn-close approval-success-close"
                            aria-label="Close"
                            onClick={handleSuccessModalClose}
                        />
                        <div ref={approvalSuccessCardRef} className="approval-success-capture-card">
                            <div className="approval-success-check" aria-hidden="true">
                                <span className="approval-success-check-icon">✓</span>
                            </div>
                            <div className="approval-success-body">
                                <div className="approval-success-avatar">
                                    {approvedEmployeePhotoUrl ? (
                                        <img
                                            src={approvedEmployeePhotoUrl}
                                            alt={approvedEmployeeSummary?.name || 'Employee'}
                                            crossOrigin="anonymous"
                                        />
                                    ) : (
                                        <span>{approvedEmployeeInitial || 'E'}</span>
                                    )}
                                </div>
                                <h4 className="approval-success-title">{successTitle}</h4>
                                <p className="approval-success-subtitle">{successSubtitle}</p>
                                <div className="approval-success-details">
                                    <div className="approval-success-detail">
                                        <span className="approval-success-detail-label">{successIdentifiers.displayLabel}</span>
                                        <strong className="approval-success-detail-value">{successIdentifiers.displayId || '—'}</strong>
                                    </div>
                                    <div className="approval-success-detail">
                                        <span className="approval-success-detail-label">Employee Name</span>
                                        <strong className="approval-success-detail-value">
                                            {approvedEmployeeSummary?.name || formData.personal.name || '—'}
                                        </strong>
                                    </div>
                                    <div className="approval-success-detail">
                                        <span className="approval-success-detail-label">Designation</span>
                                        <strong className="approval-success-detail-value">{successDesignationLabel || '—'}</strong>
                                    </div>
                                    <div className="approval-success-detail">
                                        <span className="approval-success-detail-label">Outlet</span>
                                        <strong className="approval-success-detail-value">{successOutletName || '—'}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="approval-success-actions d-flex flex-column flex-sm-row flex-wrap gap-2 justify-content-center mt-4">
                            <button
                                type="button"
                                className="btn btn-success d-inline-flex align-items-center justify-content-center gap-2"
                                onClick={handleShareApprovalToOutlet}
                                disabled={isSharingApprovalImage || isCopyingApprovalImage}
                            >
                                <FaWhatsapp aria-hidden="true" />
                                {isSharingApprovalImage ? 'Sharing...' : 'Share WhatsApp'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary d-inline-flex align-items-center justify-content-center gap-2"
                                onClick={handleCopyApprovalImage}
                                disabled={isSharingApprovalImage || isCopyingApprovalImage}
                            >
                                <FaRegCopy aria-hidden="true" />
                                {isCopyingApprovalImage ? 'Copying...' : 'Copy Image'}
                            </button>
                            {successPrimaryAction ? (
                                <button
                                    type="button"
                                    className="btn approval-success-secondary"
                                    onClick={successPrimaryAction.onClick}
                                    disabled={isSharingApprovalImage || isCopyingApprovalImage}
                                >
                                    {successPrimaryAction.label}
                                </button>
                            ) : null}
                            <button
                                type="button"
                                className="btn approval-success-primary"
                                onClick={handleSuccessModalDashboard}
                                disabled={isSharingApprovalImage || isCopyingApprovalImage}
                            >
                                Back to Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </DashboardLayout>
    );
};

export default PendingEmployeeApprovalView;
